import type { WRPayload } from "./types/wealthReader";

/** Tipos compartidos para los endpoints del backend. */

// --- Payloads ---

export type PayloadMeta = {
  filename: string;
  receivedAt: string;
  operationId: string;
  success: boolean;
  errorCode: number;
  sizeBytes: number;
};

export type PayloadListResponse = { count: number; items: PayloadMeta[] };
export type PayloadDetailResponse = { meta: PayloadMeta; body: unknown };

export async function fetchPayloads(limit = 100): Promise<PayloadListResponse> {
  const res = await fetch(`/api/payloads?limit=${limit}`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json() as Promise<PayloadListResponse>;
}

export async function fetchPayloadByOperation(opId: string): Promise<PayloadDetailResponse | null> {
  const res = await fetch(`/api/payloads/by-operation/${encodeURIComponent(opId)}`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json() as Promise<PayloadDetailResponse>;
}

export async function fetchLatestPayload(): Promise<{ meta: PayloadMeta; body: WRPayload } | null> {
  const res = await fetch(`/api/payloads/latest`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json() as Promise<{ meta: PayloadMeta; body: WRPayload }>;
}

// --- Credenciales ---

export type RefreshStatus = "never" | "ok" | "error" | "needs_2fa" | "broken";

export type Credential = {
  id: string;
  entityCode: string;
  userId: string;
  userName: string;
  label: string;
  productTypes: string;
  onlyBalances: boolean;
  movementDays: number;
  contractName: string | null;
  createdAt: string;
  updatedAt: string;
  lastRefreshAt: string | null;
  lastRefreshStatus: RefreshStatus;
  lastRefreshCode: number;
  lastRefreshMessage: string | null;
  lastOperationId: string | null;
  tokenPreview: string;
};

export type CredentialsListResponse = { count: number; items: Credential[] };

export type RefreshSyncSummary = {
  accounts: { synced: number; unmatched: number };
  loans: { synced: number; unmatched: number };
  errors: string[];
};

export type RefreshResult = {
  credentialId: string;
  ok: boolean;
  status: RefreshStatus;
  code: number;
  operationId: string | null;
  payloadFilename: string | null;
  message: string | null;
  durationMs: number;
  sync: RefreshSyncSummary | null;
};

export async function fetchCredentials(): Promise<CredentialsListResponse> {
  const res = await fetch("/api/credentials");
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json() as Promise<CredentialsListResponse>;
}

export async function deleteCredential(id: string): Promise<void> {
  const res = await fetch(`/api/credentials/${encodeURIComponent(id)}`, { method: "DELETE" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

export async function refreshCredentialApi(id: string): Promise<{
  result: RefreshResult;
  credential: Credential;
}> {
  const res = await fetch(`/api/credentials/${encodeURIComponent(id)}/refresh`, { method: "POST" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json() as Promise<{ result: RefreshResult; credential: Credential }>;
}

export type RefreshAllResponse = {
  total: number;
  ok: number;
  failed: number;
  durationMs: number;
  results: RefreshResult[];
  items: Credential[];
};

export async function refreshCredentialsByBankApi(
  bankCode: string,
  iban?: string,
): Promise<RefreshAllResponse & { bankCode: string; iban: string | null; usingIndex: boolean }> {
  const qs = iban ? `?iban=${encodeURIComponent(iban)}` : "";
  const res = await fetch(`/api/credentials/refresh-by-bank/${encodeURIComponent(bankCode)}${qs}`, {
    method: "POST",
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  return res.json() as Promise<
    RefreshAllResponse & { bankCode: string; iban: string | null; usingIndex: boolean }
  >;
}

export async function refreshAllCredentialsApi(): Promise<RefreshAllResponse> {
  const res = await fetch(`/api/credentials/refresh-all`, { method: "POST" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json() as Promise<RefreshAllResponse>;
}

export async function updateCredentialLabelApi(id: string, label: string): Promise<Credential> {
  return patchCredential(id, { label });
}

export async function patchCredential(
  id: string,
  patch: {
    label?: string;
    onlyBalances?: boolean;
    productTypes?: string;
    movementDays?: number;
    contractName?: string | null;
  },
): Promise<Credential> {
  const res = await fetch(`/api/credentials/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  const json = (await res.json()) as { credential: Credential };
  return json.credential;
}

// --- Empresas ---

export type SafeSapCompanyConfig = {
  companyDb: string;
  username: string;
  hasPassword: boolean;
};

export type Company = {
  id: string;
  name: string;
  alias: string;
  isCompany: boolean;
  notes: string;
  externalIds: string[];
  sap: SafeSapCompanyConfig | null;
  createdAt: string;
  updatedAt: string;
};

export async function fetchCompanies(): Promise<{ count: number; items: Company[] }> {
  const res = await fetch("/api/companies");
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export async function createOrUpdateCompany(input: {
  id: string;
  name: string;
  alias?: string;
  notes?: string;
}): Promise<Company> {
  const res = await fetch("/api/companies", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  const json = (await res.json()) as { company: Company };
  return json.company;
}

export async function patchCompany(
  id: string,
  patch: { alias?: string; notes?: string },
): Promise<Company> {
  const res = await fetch(`/api/companies/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = (await res.json()) as { company: Company };
  return json.company;
}

export async function addCompanyExternalId(id: string, externalId: string): Promise<Company> {
  const res = await fetch(`/api/companies/${encodeURIComponent(id)}/external-ids`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ externalId }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  const json = (await res.json()) as { company: Company };
  return json.company;
}

export async function removeCompanyExternalId(id: string, externalId: string): Promise<Company> {
  const res = await fetch(
    `/api/companies/${encodeURIComponent(id)}/external-ids/${encodeURIComponent(externalId)}`,
    { method: "DELETE" },
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = (await res.json()) as { company: Company };
  return json.company;
}

export type DeleteCompanyDependencies = {
  accounts: { iban: string; alias: string; bankCode: string }[];
  loans: { bankCode: string; contractNumber: string; alias: string }[];
};

export type DeleteCompanyResult =
  | { ok: true; dependenciesUnlinked: { accounts: number; loans: number } | null }
  | { ok: false; conflict: true; dependencies: DeleteCompanyDependencies };

export async function deleteCompany(id: string, force = false): Promise<DeleteCompanyResult> {
  const url = `/api/companies/${encodeURIComponent(id)}${force ? "?force=true" : ""}`;
  const res = await fetch(url, { method: "DELETE" });
  if (res.status === 409) {
    const json = (await res.json()) as DeleteCompanyDependencies;
    return { ok: false, conflict: true, dependencies: { accounts: json.accounts, loans: json.loans } };
  }
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  const json = (await res.json()) as { dependenciesUnlinked: { accounts: number; loans: number } | null };
  return { ok: true, dependenciesUnlinked: json.dependenciesUnlinked };
}

export async function renameCompany(
  oldId: string,
  newId: string,
  keepOldAsExternalId = true,
): Promise<{ company: Company; reassigned: { accounts: number; loans: number } }> {
  const res = await fetch(`/api/companies/${encodeURIComponent(oldId)}/rename`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ newId, keepOldAsExternalId }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  return res.json() as Promise<{ company: Company; reassigned: { accounts: number; loans: number } }>;
}

// --- Cuentas ---

export type StoredBankOwner = { ID: string | null; name: string; role: string };

export type Account = {
  iban: string;
  alias: string;
  bankCode: string;
  bankName: string;
  companyId: string;
  currency: string;
  subtype: string;
  notes: string;
  active: boolean;
  /** true si la cuenta es una PÓLIZA DE CRÉDITO. Cambia la semántica del
   *  saldo: current = dispuesto (negativo), available = crédito libre. En
   *  el total por empresa las pólizas contribuyen con el dispuesto, NO con
   *  el disponible. Editable en la ficha de la cuenta. */
  isCreditLine: boolean;
  lastSyncedAt: string | null;
  lastBalanceAvailable: number | null;
  lastBalanceCurrent: number | null;
  lastPayloadFile: string | null;
  lastBankUuid: string | null;
  lastOwnersFromBank: StoredBankOwner[];
  sapGLAccount: string;
  sapEntriesSyncFrom: string | null;
  sapEntriesLastSyncedAt: string | null;
  sapEntriesLastError: string | null;
  createdAt: string;
  updatedAt: string;
};

export async function fetchAccounts(): Promise<{ count: number; items: Account[] }> {
  const res = await fetch("/api/accounts");
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export async function createAccount(input: {
  iban: string;
  bankCode: string;
  companyId: string;
  bankName?: string;
  alias?: string;
  currency?: string;
  subtype?: string;
  notes?: string;
  active?: boolean;
}): Promise<Account> {
  const res = await fetch("/api/accounts", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  const json = (await res.json()) as { account: Account };
  return json.account;
}

export async function patchAccount(
  iban: string,
  patch: {
    alias?: string;
    bankCode?: string;
    bankName?: string;
    companyId?: string;
    currency?: string;
    subtype?: string;
    notes?: string;
    active?: boolean;
    isCreditLine?: boolean;
  },
): Promise<Account> {
  const res = await fetch(`/api/accounts/${encodeURIComponent(iban)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = (await res.json()) as { account: Account };
  return json.account;
}

export async function deleteAccount(iban: string): Promise<void> {
  const res = await fetch(`/api/accounts/${encodeURIComponent(iban)}`, { method: "DELETE" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

// --- Movimientos de cuenta ---

export type TransferDetails = {
  concept: string;
  senderReceiver: string;
  accountNumber: string;
};

export type Movement = {
  uuid: string;
  operationDate: string;
  valueDate: string;
  amount: number;
  balance: number | null;
  description: string;
  transferDetails: TransferDetails | null;
  lastPayloadFile: string;
  updatedAt: string;
  posting?: {
    jdtNum: number;
    docEntry: number | null;
    documentType: "incoming_payment" | "vendor_payment" | "journal_entry";
    postedAt: string;
    memo: string;
    /** true si el documento se creó como Borrador (SAP_DRAFT_MODE=1). En
     *  ese caso `jdtNum` es en realidad el DocEntry de ODRF y NO aparecerá
     *  en el sync de apuntes SAP hasta que alguien apruebe el borrador en
     *  SAP. */
    isDraft?: boolean;
  };
};

export type MovementsResponse = {
  iban: string;
  total: number;
  count: number;
  items: Movement[];
  account: {
    iban: string;
    alias: string;
    bankCode: string;
    bankName: string;
    companyId: string;
    currency: string;
    lastSyncedAt: string | null;
  };
};

export async function fetchAccountMovements(
  iban: string,
  params: { from?: string; to?: string; q?: string; limit?: number; offset?: number } = {},
): Promise<MovementsResponse> {
  const qs = new URLSearchParams();
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);
  if (params.q) qs.set("q", params.q);
  if (params.limit !== undefined) qs.set("limit", String(params.limit));
  if (params.offset !== undefined) qs.set("offset", String(params.offset));
  const url = `/api/accounts/${encodeURIComponent(iban)}/movements${qs.toString() ? `?${qs}` : ""}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  return res.json() as Promise<MovementsResponse>;
}

// --- Apuntes contables SAP de la cuenta ---

export type SapEntry = {
  jdtNum: number;
  lineId: number;
  accountCode: string;
  referenceDate: string;
  postingDate: string;
  dueDate: string;
  amount: number;
  debit: number;
  credit: number;
  currency: string;
  contraAccount: string;
  lineMemo: string;
  headerMemo: string;
  reference1: string;
  reference2: string;
  transactionCode: string;
  bplId: number | null;
  internalReconciliationNum: number | null;
  updatedAt: string;
};

export type SapEntriesResponse = {
  iban: string;
  glAccount: string;
  total: number;
  count: number;
  items: SapEntry[];
  syncInFlight: boolean;
  account: {
    iban: string;
    alias: string;
    bankCode: string;
    bankName: string;
    companyId: string;
    currency: string;
    sapGLAccount: string;
    sapEntriesSyncFrom: string | null;
    sapEntriesLastSyncedAt: string | null;
    sapEntriesLastError: string | null;
  };
};

/** Respuesta 202 del POST /sync: el sync arranca en background. El cliente
 *  hace polling a fetchAccountSapEntries hasta que syncInFlight vuelve a false. */
export type SapEntriesSyncStartedResponse = {
  started: boolean;
  iban: string;
  message: string;
};

export async function fetchAccountSapEntries(
  iban: string,
  params: { from?: string; to?: string; q?: string; limit?: number; offset?: number } = {},
): Promise<SapEntriesResponse> {
  const qs = new URLSearchParams();
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);
  if (params.q) qs.set("q", params.q);
  if (params.limit !== undefined) qs.set("limit", String(params.limit));
  if (params.offset !== undefined) qs.set("offset", String(params.offset));
  const url = `/api/accounts/${encodeURIComponent(iban)}/sap-entries${qs.toString() ? `?${qs}` : ""}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  return res.json() as Promise<SapEntriesResponse>;
}

// --- Auditoría de errores SAP ---

export type SapErrorItem = {
  iban: string;
  alias: string;
  bankCode: string;
  bankName: string;
  companyId: string;
  companyName: string;
  sapGLAccount: string;
  error: string;
  lastSyncedAt: string | null;
};

export async function fetchSapErrors(): Promise<{ count: number; items: SapErrorItem[] }> {
  const res = await fetch("/api/accounts/sap-errors");
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  return res.json() as Promise<{ count: number; items: SapErrorItem[] }>;
}

export async function syncAccountSapEntries(
  iban: string,
  opts: { reset?: boolean } = {},
): Promise<SapEntriesSyncStartedResponse> {
  const qs = opts.reset ? "?reset=1" : "";
  const res = await fetch(`/api/accounts/${encodeURIComponent(iban)}/sap-entries/sync${qs}`, {
    method: "POST",
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  return res.json() as Promise<SapEntriesSyncStartedResponse>;
}

// --- Préstamos ---

export type Loan = {
  bankCode: string;
  contractNumber: string;
  alias: string;
  bankName: string;
  companyId: string;
  currency: string;
  notes: string;
  active: boolean;
  /** Fragmento de texto (case-insensitive) que aparece en la descripción del
   *  movimiento del banco cuando corresponde a una cuota de ESTE préstamo.
   *  Es el criterio PRINCIPAL de detección. Si vacío, se usa el
   *  contractNumber como criterio de fallback. */
  descriptionPattern: string;
  /** Cuentas contables SAP + centros de coste para contabilizar los pagos
   *  del préstamo. La cuota se descompone en LP, CP, intereses y comisiones. */
  sapAccountLongTerm: string;
  costingCodeLongTerm: string;
  sapAccountShortTerm: string;
  costingCodeShortTerm: string;
  sapAccountInterest: string;
  costingCodeInterest: string;
  sapAccountFees: string;
  costingCodeFees: string;
  /** Cuenta puente para cuotas parcialmente pagadas (cargo por falta de saldo).
   *  Vacío = no se ofrece la opción de "pago parcial" para este préstamo. */
  sapAccountBridge: string;
  costingCodeBridge: string;
  /** Resumen del cuadro de amortización (solo lo rellena GET /loans). */
  summary?: LoanSummary | null;
  lastSyncedAt: string | null;
  lastGrantedCapital: number | null;
  lastPendingAmount: number | null;
  lastInterestRate: number | null;
  lastExpirationDate: string | null;
  lastNextInstallment: number | null;
  lastPayloadFile: string | null;
  lastBankUuid: string | null;
  createdAt: string;
  updatedAt: string;
};

/** Datos derivados del cuadro de amortización, mostrados en el listado. */
export type LoanSummary = {
  /** Fecha (ISO) de la siguiente cuota pendiente. */
  nextInstallmentDate: string | null;
  /** Importe total (`total`) de esa siguiente cuota. */
  nextInstallmentAmount: number | null;
  /** Suma de `amortized` de las próximas 12 cuotas pendientes. */
  pendingCapitalShortTerm: number;
  /** Suma de `amortized` de las cuotas pendientes >12. */
  pendingCapitalLongTerm: number;
};

export async function fetchLoans(): Promise<{ count: number; items: Loan[] }> {
  const res = await fetch("/api/loans");
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export async function createLoan(input: {
  bankCode: string;
  contractNumber: string;
  companyId: string;
  alias?: string;
  bankName?: string;
  currency?: string;
  notes?: string;
  active?: boolean;
}): Promise<Loan> {
  const res = await fetch("/api/loans", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  const json = (await res.json()) as { loan: Loan };
  return json.loan;
}

export async function patchLoan(
  bankCode: string,
  contractNumber: string,
  patch: {
    alias?: string;
    bankName?: string;
    companyId?: string;
    currency?: string;
    notes?: string;
    active?: boolean;
    descriptionPattern?: string;
    sapAccountLongTerm?: string;
    costingCodeLongTerm?: string;
    sapAccountShortTerm?: string;
    costingCodeShortTerm?: string;
    sapAccountInterest?: string;
    costingCodeInterest?: string;
    sapAccountFees?: string;
    costingCodeFees?: string;
    sapAccountBridge?: string;
    costingCodeBridge?: string;
  },
): Promise<Loan> {
  const res = await fetch(
    `/api/loans/${encodeURIComponent(bankCode)}/${encodeURIComponent(contractNumber.trim())}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    },
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  const json = (await res.json()) as { loan: Loan };
  return json.loan;
}

export async function deleteLoan(bankCode: string, contractNumber: string): Promise<void> {
  const res = await fetch(
    `/api/loans/${encodeURIComponent(bankCode)}/${encodeURIComponent(contractNumber.trim())}`,
    { method: "DELETE" },
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

// --- Leasings (maestro manual) ---

export type Leasing = {
  bankCode: string;
  contractNumber: string;
  alias: string;
  bankName: string;
  companyId: string;
  currency: string;
  notes: string;
  active: boolean;
  iban: string;
  supplier: string;
  supplierCardCode: string;
  descriptionPattern: string;
  sapAccountAsset: string;
  costingCodeAsset: string;
  projectCodeAsset: string;
  sapAccountVAT: string;
  costingCodeVAT: string;
  projectCodeVAT: string;
  sapAccountLongTerm: string;
  costingCodeLongTerm: string;
  projectCodeLongTerm: string;
  sapAccountShortTerm: string;
  costingCodeShortTerm: string;
  projectCodeShortTerm: string;
  sapAccountInterest: string;
  costingCodeInterest: string;
  projectCodeInterest: string;
  sapAccountFees: string;
  costingCodeFees: string;
  projectCodeFees: string;
  /** Código IVA/TaxCode (VatGroup) para las líneas de la factura de leasing
   *  cuando se contabiliza. Vacío = SAP lo deduce del proveedor. Fase 3. */
  vatGroup: string;
  /** Parámetros del contrato — alimentan el cuadro de amortización. */
  startDate: string | null;
  principal: number | null;
  installmentCount: number | null;
  interestRateAnnual: number | null;
  vatRatePercent: number | null;
  residualValue: number | null;
  postalFeeMonthly: number | null;
  /** Tramos de tipo escalonado. Cada tramo aplica desde `fromInstallment`
   *  hasta el fromInstallment del siguiente tramo (o hasta N). Reglas:
   *   - `fixedBase`: cuota fija del tramo (€) → sobrescribe la francesa.
   *   - `initialInterest`: interés del primer mes del tramo (€) → se deduce
   *     `tipo_mensual = initialInterest / capital_pdte` al iniciar el tramo.
   *   - `annualRate`: fallback si no hay `initialInterest`. */
  interestRateTranches: Array<{
    fromInstallment: number;
    annualRate: number;
    fixedBase?: number;
    initialInterest?: number;
  }> | null;
  /** Fórmula del interés usada por este banco/contrato. */
  interestFormula: "standard" | "bank_next" | "average";
  createdAt: string;
  updatedAt: string;
  /** Resumen calculado desde el cuadro (solo lo rellena GET /leasings). */
  summary?: LeasingSummary | null;
};

/** Datos derivados del cuadro, mostrados en el listado de leasings. */
export type LeasingSummary = {
  /** Fecha (ISO) de la siguiente cuota no pagada. null si el cuadro está vacío o todo pagado. */
  nextInstallmentDate: string | null;
  /** Importe totalPayment de esa siguiente cuota. */
  nextInstallmentAmount: number | null;
  /** Suma de `amortized` de las próximas 12 cuotas pendientes. */
  pendingCapitalShortTerm: number;
  /** Suma de `amortized` de las cuotas pendientes >12. */
  pendingCapitalLongTerm: number;
  /** Fecha (ISO) de la última cuota con isResidual=true. */
  residualDate: string | null;
};

export type InterestFormula = "standard" | "bank_next" | "average";

export type FormulaDetectionResult = {
  ok: true;
  suggested: InterestFormula;
  confidence: "high" | "medium" | "low";
  reasoning: string;
  matches: Array<{ formula: InterestFormula; snippet: string }>;
  textSample: string;
  fileName: string | null;
};

export async function detectLeasingFormulaFromPdf(
  bankCode: string,
  contractNumber: string,
  file: File,
): Promise<FormulaDetectionResult> {
  const url = `/api/leasings/${encodeURIComponent(bankCode)}/${encodeURIComponent(contractNumber.trim())}/detect-formula`;
  const form = new FormData();
  form.append("pdf", file);
  const res = await fetch(url, { method: "POST", body: form });
  const text = await res.text();
  if (!res.ok) throw new Error(text);
  return JSON.parse(text) as FormulaDetectionResult;
}

export async function fetchLeasings(): Promise<{ count: number; items: Leasing[] }> {
  const res = await fetch("/api/leasings");
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export async function createLeasing(input: {
  bankCode: string;
  contractNumber: string;
  companyId: string;
  alias?: string;
  bankName?: string;
  currency?: string;
  notes?: string;
  active?: boolean;
  iban?: string;
  supplier?: string;
  supplierCardCode?: string;
  descriptionPattern?: string;
}): Promise<Leasing> {
  const res = await fetch("/api/leasings", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  const json = (await res.json()) as { leasing: Leasing };
  return json.leasing;
}

export async function patchLeasing(
  bankCode: string,
  contractNumber: string,
  patch: Partial<{
    alias: string;
    bankCode: string;
    bankName: string;
    companyId: string;
    currency: string;
    notes: string;
    active: boolean;
    iban: string;
    supplier: string;
    supplierCardCode: string;
    descriptionPattern: string;
    sapAccountAsset: string;
    costingCodeAsset: string;
    projectCodeAsset: string;
    sapAccountVAT: string;
    costingCodeVAT: string;
    projectCodeVAT: string;
    sapAccountLongTerm: string;
    costingCodeLongTerm: string;
    projectCodeLongTerm: string;
    sapAccountShortTerm: string;
    costingCodeShortTerm: string;
    projectCodeShortTerm: string;
    sapAccountInterest: string;
    costingCodeInterest: string;
    projectCodeInterest: string;
    sapAccountFees: string;
    costingCodeFees: string;
    projectCodeFees: string;
    vatGroup: string;
    startDate: string | null;
    principal: number | null;
    installmentCount: number | null;
    interestRateAnnual: number | null;
    vatRatePercent: number | null;
    residualValue: number | null;
    postalFeeMonthly: number | null;
    interestRateTranches: Array<{
      fromInstallment: number;
      annualRate: number;
      fixedBase?: number;
      initialInterest?: number;
    }> | null;
    interestFormula: "standard" | "bank_next" | "average";
  }>,
): Promise<Leasing> {
  const res = await fetch(
    `/api/leasings/${encodeURIComponent(bankCode)}/${encodeURIComponent(contractNumber.trim())}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    },
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  const json = (await res.json()) as { leasing: Leasing };
  return json.leasing;
}

export async function deleteLeasing(bankCode: string, contractNumber: string): Promise<void> {
  const res = await fetch(
    `/api/leasings/${encodeURIComponent(bankCode)}/${encodeURIComponent(contractNumber.trim())}`,
    { method: "DELETE" },
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

/**
 * Corrige el nº de contrato de un leasing (errores tipográficos al alta).
 * Mueve maestro + schedule a la nueva clave. Falla con 409 si el nuevo
 * contrato ya existe en el mismo banco.
 */
export async function renameLeasingContractApi(
  bankCode: string,
  oldContractNumber: string,
  newContractNumber: string,
): Promise<Leasing> {
  const res = await fetch(
    `/api/leasings/${encodeURIComponent(bankCode)}/${encodeURIComponent(oldContractNumber)}/rename`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ newContractNumber }),
    },
  );
  const text = await res.text();
  if (!res.ok) {
    let msg = text;
    try {
      const p = JSON.parse(text) as { message?: string };
      if (p.message) msg = p.message;
    } catch { /* keep */ }
    throw new Error(msg);
  }
  const p = JSON.parse(text) as { leasing: Leasing };
  return p.leasing;
}

/** Autocompleta el nombre del proveedor consultando OCRD via HANA por
 *  CardCode. Devuelve null si el BP no existe en la CompanyDB. */
export async function fetchBusinessPartnerByCardCode(
  companyId: string,
  cardCode: string,
): Promise<{ cardCode: string; cardName: string; cardType: string } | null> {
  const url = `/api/sap/companies/${encodeURIComponent(companyId)}/business-partner/${encodeURIComponent(cardCode)}`;
  const res = await fetch(url);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  return res.json() as Promise<{ cardCode: string; cardName: string; cardType: string }>;
}

/** Busca interlocutores por fragmento de nombre en OCRD (HANA). Mínimo 3
 *  caracteres; el backend devuelve hasta 20 resultados. */
export type BusinessPartnerHit = {
  cardCode: string;
  cardName: string;
  cardType: string;
};
export async function searchBusinessPartnersByName(
  companyId: string,
  name: string,
  type: "customer" | "supplier" | "any" = "any",
): Promise<BusinessPartnerHit[]> {
  const url =
    `/api/sap/companies/${encodeURIComponent(companyId)}/business-partners` +
    `?name=${encodeURIComponent(name)}&type=${encodeURIComponent(type)}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  const j = (await res.json()) as { items: BusinessPartnerHit[] };
  return j.items ?? [];
}

/** Lista de partidas abiertas (OINV/OPCH) para un socio concreto. Se usa
 *  desde el modal de contabilización cuando el usuario ya sabe qué
 *  interlocutor debe recibir el pago/cobro. */
export async function fetchOpenInvoicesByCardCode(
  companyId: string,
  cardCode: string,
  source: "invoices" | "purchase_invoices" | "auto" = "auto",
): Promise<PostingCandidateInvoice[]> {
  const url =
    `/api/sap/companies/${encodeURIComponent(companyId)}/open-invoices` +
    `?cardCode=${encodeURIComponent(cardCode)}&source=${encodeURIComponent(source)}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  const j = (await res.json()) as { items: Array<Omit<PostingCandidateInvoice, "score" | "matchedBy">> };
  return (j.items ?? []).map((it) => ({
    ...it,
    score: 0,
    matchedBy: [],
  }));
}

// --- Cuadro de amortización del leasing ---

export type LeasingInstallment = {
  index: number;
  date: string;
  base: number;
  interest: number;
  amortized: number;
  vat: number;
  postalFee: number;
  /** Total factura = base + IVA (sin envío). */
  total: number;
  /** Total pago = base + envío + IVA. Es lo que se carga en cuenta. */
  totalPayment: number;
  pendingCapital: number;
  isResidual: boolean;
  paidAt: string | null;
  paidJdtNum: number | null;
  /** PROVISIONAL fase implantación: true si el usuario lo marcó a mano. */
  manuallyMarked?: boolean;
  /** Cuota partida por falta de saldo: importe pagado acumulado. */
  paidAmount?: number;
  /** DocEntry de la PurchaseInvoice creada en el primer parcial. Se
   *  reutiliza para los cargos residuales. */
  invoiceDocEntry?: number | null;
  /** "pending" (default) | "partial" (factura creada + payment parcial) | "paid". */
  status?: "pending" | "partial" | "paid";
};

export type LeasingScheduleEditableField =
  | "date"
  | "base"
  | "total"
  | "interest"
  | "amortized"
  | "postalFee";

export type LeasingScheduleResponse = {
  leasing: {
    bankCode: string;
    contractNumber: string;
    alias: string;
    companyId: string;
    currency: string;
    supplier: string;
    supplierCardCode: string;
    startDate: string | null;
    principal: number | null;
    installmentCount: number | null;
    interestRateAnnual: number | null;
    vatRatePercent: number | null;
    residualValue: number | null;
    postalFeeMonthly: number | null;
    interestRateTranches: Array<{ fromInstallment: number; annualRate: number }> | null;
  };
  installments: LeasingInstallment[];
  generatedAt: string | null;
  paramsSnapshot: {
    startDate: string;
    principal: number;
    installmentCount: number;
    interestRateAnnual: number;
    vatRatePercent: number;
    residualValue: number;
    postalFeeMonthly: number;
    interestRateTranches?: Array<{ fromInstallment: number; annualRate: number }>;
  } | null;
};

export async function fetchLeasingSchedule(
  bankCode: string,
  contractNumber: string,
): Promise<LeasingScheduleResponse> {
  const url = `/api/leasings/${encodeURIComponent(bankCode)}/${encodeURIComponent(contractNumber.trim())}/schedule`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  return res.json() as Promise<LeasingScheduleResponse>;
}

export async function regenerateLeasingSchedule(
  bankCode: string,
  contractNumber: string,
): Promise<{ ok: true; installmentCount: number; generatedAt: string }> {
  const url = `/api/leasings/${encodeURIComponent(bankCode)}/${encodeURIComponent(contractNumber.trim())}/schedule/regenerate`;
  const res = await fetch(url, { method: "POST" });
  const text = await res.text();
  if (!res.ok) {
    let msg = text;
    try {
      const p = JSON.parse(text) as { message?: string };
      if (p.message) msg = p.message;
    } catch { /* keep */ }
    throw new Error(msg);
  }
  return JSON.parse(text) as { ok: true; installmentCount: number; generatedAt: string };
}

/**
 * Edita una celda de la cuota `atIndex` en el cuadro. Backend recalcula
 * en cascada las cuotas posteriores usando los parámetros del contrato.
 */
export async function patchLeasingInstallment(
  bankCode: string,
  contractNumber: string,
  atIndex: number,
  field: LeasingScheduleEditableField,
  value: number | string,
): Promise<{ ok: true; installmentCount: number; atIndex: number; field: string }> {
  const url = `/api/leasings/${encodeURIComponent(bankCode)}/${encodeURIComponent(contractNumber.trim())}/schedule/installment/${encodeURIComponent(String(atIndex))}`;
  const res = await fetch(url, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ field, value }),
  });
  const text = await res.text();
  if (!res.ok) {
    let msg = text;
    try {
      const p = JSON.parse(text) as { message?: string };
      if (p.message) msg = p.message;
    } catch { /* keep */ }
    throw new Error(msg);
  }
  return JSON.parse(text) as { ok: true; installmentCount: number; atIndex: number; field: string };
}

/**
 * Marca / desmarca manualmente una cuota como pagada (fase implantación).
 * Provisional — se retirará cuando la implantación termine.
 */
export async function markLeasingInstallmentManualApi(
  bankCode: string,
  contractNumber: string,
  atIndex: number,
  paidAt: string,
): Promise<void> {
  const url = `/api/leasings/${encodeURIComponent(bankCode)}/${encodeURIComponent(contractNumber.trim())}/schedule/installment/${encodeURIComponent(String(atIndex))}/mark-manual`;
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ paidAt }),
  });
  if (!res.ok) {
    const text = await res.text();
    let msg = text;
    try {
      const p = JSON.parse(text) as { message?: string };
      if (p.message) msg = p.message;
    } catch { /* keep */ }
    throw new Error(msg);
  }
}

export async function unmarkLeasingInstallmentManualApi(
  bankCode: string,
  contractNumber: string,
  atIndex: number,
): Promise<void> {
  const url = `/api/leasings/${encodeURIComponent(bankCode)}/${encodeURIComponent(contractNumber.trim())}/schedule/installment/${encodeURIComponent(String(atIndex))}/mark-manual`;
  const res = await fetch(url, { method: "DELETE" });
  if (!res.ok) {
    const text = await res.text();
    let msg = text;
    try {
      const p = JSON.parse(text) as { message?: string };
      if (p.message) msg = p.message;
    } catch { /* keep */ }
    throw new Error(msg);
  }
}

/** Candidato de PurchaseInvoice devuelto por el lookup SAP para "Reabrir parcial". */
export type LeasingInvoiceCandidate = {
  docEntry: number;
  docNum: number;
  docDate: string;
  docTotal: number;
  paidToDate: number;
  documentStatus: string;
  comments: string;
  cardCode: string;
  cardName: string;
  /** docTotal - paidToDate. Es lo que se usará como paidAmount al reabrir. */
  residualExpected: number;
};

/**
 * Busca en SAP PurchaseInvoices candidatas para una cuota concreta del leasing.
 * Filtra por CardCode del proveedor + fecha ±14d + total ±10€. Devuelve lista
 * ordenada por DocDate desc.
 */
export async function fetchLeasingInvoiceCandidatesApi(
  bankCode: string,
  contractNumber: string,
  atIndex: number,
): Promise<LeasingInvoiceCandidate[]> {
  const url = `/api/leasings/${encodeURIComponent(bankCode)}/${encodeURIComponent(contractNumber.trim())}/schedule/installment/${encodeURIComponent(String(atIndex))}/invoice-candidates`;
  const res = await fetch(url);
  const text = await res.text();
  if (!res.ok) {
    let msg = text;
    try {
      const p = JSON.parse(text) as { message?: string };
      if (p.message) msg = p.message;
    } catch { /* keep */ }
    throw new Error(msg);
  }
  const parsed = JSON.parse(text) as { candidates: LeasingInvoiceCandidate[] };
  return parsed.candidates ?? [];
}

/**
 * Reabre una cuota ya contabilizada por la app como `partial`. Se usa para
 * reparar datos "heredados" donde el schedule dice paid pero SAP tiene la
 * factura parcialmente pagada. Requiere `paidAmount` (lo ya pagado, < total)
 * y `invoiceDocEntry` (la factura ya emitida en SAP).
 */
export async function reopenLeasingInstallmentAsPartialApi(
  bankCode: string,
  contractNumber: string,
  atIndex: number,
  paidAmount: number,
  invoiceDocEntry: number,
): Promise<void> {
  const url = `/api/leasings/${encodeURIComponent(bankCode)}/${encodeURIComponent(contractNumber.trim())}/schedule/installment/${encodeURIComponent(String(atIndex))}/reopen-partial`;
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ paidAmount, invoiceDocEntry }),
  });
  if (!res.ok) {
    const text = await res.text();
    let msg = text;
    try {
      const p = JSON.parse(text) as { message?: string };
      if (p.message) msg = p.message;
    } catch { /* keep */ }
    throw new Error(msg);
  }
}

/** Resuelve el DocEntry de una PurchaseInvoice a partir de su Nº de factura
 *  (DocNum) visible en SAP, filtrado por el CardCode del proveedor del leasing. */
export async function resolveLeasingInvoiceByDocNumApi(
  bankCode: string,
  contractNumber: string,
  atIndex: number,
  docNum: number,
  opts: { anyCardCode?: boolean } = {},
): Promise<LeasingInvoiceCandidate[]> {
  const q = new URLSearchParams({ docNum: String(docNum) });
  if (opts.anyCardCode) q.set("anyCardCode", "1");
  const url = `/api/leasings/${encodeURIComponent(bankCode)}/${encodeURIComponent(contractNumber.trim())}/schedule/installment/${encodeURIComponent(String(atIndex))}/invoice-by-docnum?${q.toString()}`;
  const res = await fetch(url);
  const text = await res.text();
  if (!res.ok) {
    let msg = text;
    try {
      const p = JSON.parse(text) as { message?: string };
      if (p.message) msg = p.message;
    } catch { /* keep */ }
    throw new Error(msg);
  }
  const parsed = JSON.parse(text) as { candidates: LeasingInvoiceCandidate[] };
  return parsed.candidates ?? [];
}

/** PATCH del invoiceDocEntry de una cuota (partial/paid). Pasa `null` para
 *  eliminarlo (la cuota queda sin factura vinculada). */
export async function setLeasingInstallmentInvoiceDocEntryApi(
  bankCode: string,
  contractNumber: string,
  atIndex: number,
  invoiceDocEntry: number | null,
): Promise<void> {
  const url = `/api/leasings/${encodeURIComponent(bankCode)}/${encodeURIComponent(contractNumber.trim())}/schedule/installment/${encodeURIComponent(String(atIndex))}/invoice-doc-entry`;
  const res = await fetch(url, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ invoiceDocEntry }),
  });
  if (!res.ok) {
    const text = await res.text();
    let msg = text;
    try {
      const p = JSON.parse(text) as { message?: string };
      if (p.message) msg = p.message;
    } catch { /* keep */ }
    throw new Error(msg);
  }
}

export type LeasingPdfPreview = {
  ok: true;
  dryRun: true;
  fileName: string | null;
  rowsCount: number;
  rows: LeasingInstallment[];
  extractedTextLength: number;
  extractedTextSample: string;
};

/** Analiza el PDF y devuelve preview SIN guardar. */
export async function previewLeasingSchedulePdf(
  bankCode: string,
  contractNumber: string,
  file: File,
): Promise<LeasingPdfPreview> {
  const url = `/api/leasings/${encodeURIComponent(bankCode)}/${encodeURIComponent(contractNumber.trim())}/schedule/import-pdf?dryRun=1`;
  const form = new FormData();
  form.append("pdf", file);
  const res = await fetch(url, { method: "POST", body: form });
  const text = await res.text();
  if (!res.ok) throw new Error(text);
  return JSON.parse(text) as LeasingPdfPreview;
}

/** Sube el PDF y reemplaza el cuadro tal cual. */
export async function importLeasingSchedulePdf(
  bankCode: string,
  contractNumber: string,
  file: File,
): Promise<{ ok: true; installmentCount: number; generatedAt: string; fileName: string | null }> {
  const url = `/api/leasings/${encodeURIComponent(bankCode)}/${encodeURIComponent(contractNumber.trim())}/schedule/import-pdf`;
  const form = new FormData();
  form.append("pdf", file);
  const res = await fetch(url, { method: "POST", body: form });
  const text = await res.text();
  if (!res.ok) {
    let msg = text;
    try {
      const p = JSON.parse(text) as { message?: string; debug?: string };
      if (p.message) msg = p.message + (p.debug ? `\n\nMuestra del texto extraído:\n${p.debug.slice(0, 800)}` : "");
    } catch { /* keep */ }
    throw new Error(msg);
  }
  return JSON.parse(text) as { ok: true; installmentCount: number; generatedAt: string; fileName: string | null };
}

// --- Cuadro de amortización (préstamos) ---

export type AmortizationInstallment = {
  date: string;
  amortized: number;
  interest: number;
  total: number;
  pendingCapital: number;
  operationType: string;
  /** Importe pagado acumulado (cuotas partidas por falta de saldo). */
  paidAmount?: number;
  /** "pending" (default) | "partial" (parcial + puente) | "paid" (cerrada). */
  status?: "pending" | "partial" | "paid";
  /** ISO cuando pasó a paid. */
  paidAt?: string | null;
  /** JdtNum del asiento que la cerró. */
  paidJdtNum?: number | null;
};

export type AmortizationResponse = {
  loan: {
    bankCode: string;
    contractNumber: string;
    alias: string;
    bankName: string;
    companyId: string;
    currency: string;
    lastGrantedCapital: number | null;
    lastPendingAmount: number | null;
    lastInterestRate: number | null;
    lastSyncedAt: string | null;
  };
  made: AmortizationInstallment[];
  pending: AmortizationInstallment[];
  lastPayloadFile: string | null;
  updatedAt: string | null;
};

export async function fetchLoanAmortization(
  bankCode: string,
  contractNumber: string,
): Promise<AmortizationResponse> {
  const url = `/api/loans/${encodeURIComponent(bankCode)}/${encodeURIComponent(contractNumber.trim())}/amortization`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  return res.json() as Promise<AmortizationResponse>;
}

export type LoanRefreshFullResult = {
  ok: true;
  credentialId: string;
  madeCount: number;
  pendingCount: number;
  /** Cuantas filas trajo el banco antes de descartes en el mapper. Si
   *  rawPendingCount es sospechosamente bajo (< 12) es que el banco recorta
   *  el cuadro. */
  rawMadeCount: number;
  rawPendingCount: number;
  /** Cuántas cuotas se han añadido por extrapolación (amortización francesa)
   *  para llegar al mínimo. Son estimadas, no oficiales del banco. */
  estimatedCount: number;
  payloadFile: string | null;
  updatedAt: string;
};

/** Detalle de un intento fallido de refresh-full, para diagnóstico
 *  (típicamente Bankinter empresa con varios contract_name). */
export type LoanRefreshFullAttempt = {
  credentialId: string;
  contractName: string | null;
  userName: string;
  label: string;
  loansSeen: string[] | null;
  error: string | null;
};

export type LoanRefreshFullFailure = {
  error: "refresh_not_found" | string;
  bankCode: string;
  contractNumber: string;
  triedCount: number;
  attempts: LoanRefreshFullAttempt[];
  message: string;
};

/** Llama a Wealth Reader con only_balances=false para bajar el cuadro
 *  completo (amortization_pending + amortization_made) del préstamo. */
export async function refreshLoanFull(
  bankCode: string,
  contractNumber: string,
): Promise<LoanRefreshFullResult> {
  const url = `/api/loans/${encodeURIComponent(bankCode)}/${encodeURIComponent(contractNumber.trim())}/refresh-full`;
  const res = await fetch(url, { method: "POST" });
  if (!res.ok) {
    const text = await res.text();
    let parsed: LoanRefreshFullFailure | null = null;
    try {
      parsed = JSON.parse(text) as LoanRefreshFullFailure;
    } catch {
      // fallthrough
    }
    if (parsed && parsed.attempts) {
      const err = new Error(parsed.message) as Error & { failure: LoanRefreshFullFailure };
      err.failure = parsed;
      throw err;
    }
    throw new Error(`HTTP ${res.status}: ${text}`);
  }
  return res.json() as Promise<LoanRefreshFullResult>;
}

export type ImportBankScheduleRow = {
  date: string;
  amortized: number;
  interest: number;
  total: number;
  pendingCapital: number;
};

export type ImportBankScheduleResult = {
  ok: true;
  pendingCount: number;
  importedCount: number;
  projectedCount: number;
  updatedAt: string;
};

/** Sustituye el cuadro pendiente por una tabla real pegada desde el banco
 *  (corrige día de cargo, reparto capital/interés, etc. cuando la
 *  proyección francesa automática diverge de lo que el banco cobra). */
export async function importLoanAmortizationPending(
  bankCode: string,
  contractNumber: string,
  rows: ImportBankScheduleRow[],
  interestRateAnnual?: number,
): Promise<ImportBankScheduleResult> {
  const url = `/api/loans/${encodeURIComponent(bankCode)}/${encodeURIComponent(contractNumber.trim())}/amortization/import-pending`;
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ rows, ...(interestRateAnnual != null ? { interestRateAnnual } : {}) }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  return res.json() as Promise<ImportBankScheduleResult>;
}

export type EditPendingInstallmentResult = {
  ok: true;
  pendingCount: number;
  updatedAt: string;
};

/** Corrige UNA cuota pendiente a mano (fecha y/o reparto capital/interés).
 *  Las cuotas posteriores mantienen su propio amortizado/interés; solo se
 *  les traslada el desfase de capital pendiente resultante. */
export async function editLoanPendingInstallment(
  bankCode: string,
  contractNumber: string,
  originalDate: string,
  patch: { date: string; amortized: number; interest: number },
): Promise<EditPendingInstallmentResult> {
  const url = `/api/loans/${encodeURIComponent(bankCode)}/${encodeURIComponent(contractNumber.trim())}/amortization/pending/${encodeURIComponent(originalDate)}`;
  const res = await fetch(url, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  return res.json() as Promise<EditPendingInstallmentResult>;
}

// --- Sin vincular (unmatched) ---

export type UnmatchedAccount = {
  id: string;
  kind: "account";
  iban: string;
  bankCode: string;
  bankAccountName: string;
  subtype: string;
  currency: string;
  ownersFromBank: StoredBankOwner[];
  suggestedCompanyId: string;
  suggestedCompanyName: string;
  /** Empresa real (id principal) tras resolver suggestedCompanyId contra
   *  los externalIds de las empresas dadas de alta. Vacío si no hay match. */
  resolvedCompanyId: string;
  resolvedCompanyName: string;
  balanceAvailable: number;
  balanceCurrent: number;
  bankUuid: string;
  firstSeenAt: string;
  lastSeenAt: string;
  lastPayloadFile: string;
};

export type UnmatchedLoan = {
  id: string;
  kind: "loan";
  bankCode: string;
  contractNumber: string;
  loanType: string;
  currency: string;
  grantedCapital: number;
  pendingAmount: number;
  expirationDate: string;
  holdersFromBank: { id: string; name: string }[];
  suggestedCompanyId: string;
  suggestedCompanyName: string;
  resolvedCompanyId: string;
  resolvedCompanyName: string;
  bankUuid: string;
  firstSeenAt: string;
  lastSeenAt: string;
  lastPayloadFile: string;
};

export type Unmatched = UnmatchedAccount | UnmatchedLoan;

export async function fetchUnmatched(): Promise<{ count: number; items: Unmatched[] }> {
  const res = await fetch("/api/unmatched");
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export async function deleteUnmatched(id: string): Promise<void> {
  const res = await fetch(`/api/unmatched/${encodeURIComponent(id)}`, { method: "DELETE" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

export async function promoteUnmatched(
  id: string,
  body: { companyId?: string; alias?: string; bankName?: string; notes?: string },
): Promise<{ kind: "account" | "loan"; account?: Account; loan?: Loan }> {
  const res = await fetch(`/api/unmatched/${encodeURIComponent(id)}/promote`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  return res.json();
}

export async function rescanPayloads(): Promise<{
  ok: boolean;
  payloadsProcessed: number;
  accounts: { synced: number; unmatched: number };
  loans: { synced: number; unmatched: number };
  errors: string[];
}> {
  const res = await fetch("/api/unmatched/rescan", { method: "POST" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

// --- Vista agregada ---

export type AccountWithSap = Account & {
  /** Suma con signo (Debit − Credit) de los apuntes SAP importados de esta
   *  cuenta. Null si aún no se ha sincronizado ningún apunte. */
  sapBalance: number | null;
  /** Fecha (YYYY-MM-DD) hasta la que la cuenta está conciliada: máximo del
   *  sello de saldo (balanceSealDate) y la fecha del último match 1:1. */
  lastReconciledDate: string | null;
  /** Balance persistido del movimiento bancario más reciente (Wealth Reader
   *  guarda `balance` en cada Movement). En pólizas suele traer el
   *  dispuesto real aunque el conector no envíe `balance.current`. */
  lastMovementBalance: number | null;
};

export type CompanyAggregate = {
  id: string;
  name: string;
  alias: string;
  isCompany: boolean;
  notes: string;
  accounts: AccountWithSap[];
  loans: Loan[];
  totalsByCurrency: Record<string, { available: number; loansPending: number }>;
  bankCodes: string[];
  hasSap: boolean;
};

export async function fetchCompaniesAggregated(): Promise<{ count: number; items: CompanyAggregate[] }> {
  const res = await fetch("/api/companies/aggregated");
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

// --- SAP Business One Service Layer (por empresa) ---

export type SapGlobalConfig = {
  host: string;
  verifySsl: boolean;
  timeoutMs: number;
  pageSize: number;
};

export type SapCompanyListItem = {
  id: string;
  name: string;
  alias: string;
  sap: SafeSapCompanyConfig | null;
  session: { connected: boolean; sessionIdPreview: string };
};

export type SapCompanyStatus = {
  companyId: string;
  configured: boolean;
  companyDb: string | null;
  connected: boolean;
  sessionIdPreview: string;
  host: string;
};

export type SapPingResponse = {
  ok: boolean;
  companyId?: string;
  host?: string;
  companyDb?: string;
  username?: string;
  sessionIdPreview?: string;
  verifySsl?: boolean;
  error?: string;
};

export type SapEntityResponse = {
  companyId: string;
  entity: string;
  top: number;
  skip: number;
  value: Record<string, unknown>[];
  nextSkip: number | null;
};

export async function sapGlobalConfig(): Promise<SapGlobalConfig> {
  const res = await fetch("/api/sap/config");
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json() as Promise<SapGlobalConfig>;
}

export async function sapCompaniesList(): Promise<{ count: number; items: SapCompanyListItem[] }> {
  const res = await fetch("/api/sap/companies");
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json() as Promise<{ count: number; items: SapCompanyListItem[] }>;
}

export async function sapSetCompanyConfig(
  companyId: string,
  patch: { companyDb: string; username: string; password?: string },
): Promise<Company> {
  const res = await fetch(`/api/sap/companies/${encodeURIComponent(companyId)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  const json = (await res.json()) as { company: Company };
  return json.company;
}

export async function sapClearCompanyConfig(companyId: string): Promise<Company> {
  const res = await fetch(`/api/sap/companies/${encodeURIComponent(companyId)}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  const json = (await res.json()) as { company: Company };
  return json.company;
}

export async function sapCompanyPing(companyId: string): Promise<SapPingResponse> {
  const res = await fetch(`/api/sap/companies/${encodeURIComponent(companyId)}/ping`);
  const body = (await res.json()) as SapPingResponse;
  return body;
}

export async function sapCompanyStatus(companyId: string): Promise<SapCompanyStatus> {
  const res = await fetch(`/api/sap/companies/${encodeURIComponent(companyId)}/status`);
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  return res.json() as Promise<SapCompanyStatus>;
}

export async function sapCompanyLogout(companyId: string): Promise<void> {
  const res = await fetch(`/api/sap/companies/${encodeURIComponent(companyId)}/logout`, {
    method: "POST",
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

export async function sapCompanyEntity(
  companyId: string,
  name: string,
  params: {
    top?: number;
    skip?: number;
    filter?: string;
    select?: string;
    orderBy?: string;
    expand?: string;
  } = {},
): Promise<SapEntityResponse> {
  const qs = new URLSearchParams();
  if (params.top !== undefined) qs.set("top", String(params.top));
  if (params.skip !== undefined) qs.set("skip", String(params.skip));
  if (params.filter) qs.set("filter", params.filter);
  if (params.select) qs.set("select", params.select);
  if (params.orderBy) qs.set("orderBy", params.orderBy);
  if (params.expand) qs.set("expand", params.expand);
  const url =
    `/api/sap/companies/${encodeURIComponent(companyId)}/entity/${encodeURIComponent(name)}` +
    (qs.toString() ? `?${qs}` : "");
  const res = await fetch(url);
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`HTTP ${res.status}: ${text}`);
  }
  return res.json() as Promise<SapEntityResponse>;
}

// --- Conciliación banco ↔ SAP ---

export type ReconciliationMethod = "auto" | "manual";

export type ReconciliationMatch = {
  movementUuid: string;
  jdtNum: number;
  lineId: number;
  matchedAt: string;
  method: ReconciliationMethod;
};

export type ReconciliationSnapshot = {
  iban: string;
  totals: {
    movements: number;
    sapEntries: number;
    matches: number;
    conciliatedMovements: number;
    conciliatedSapEntries: number;
    /** movimientos con fecha <= balanceSealDate. */
    sealedMovements: number;
    /** apuntes SAP con fecha <= balanceSealDate. */
    sealedSapEntries: number;
    pendingMovements: number;
    pendingSapEntries: number;
  };
  /** Fecha (YYYY-MM-DD) hasta la que el saldo del banco coincide con el
   *  saldo del mayor SAP. Todo item con fecha <= balanceSealDate se
   *  considera "casado por saldo". null si no hay coincidencia. */
  balanceSealDate: string | null;
  movements: Movement[];
  sapEntries: SapEntry[];
  matches: ReconciliationMatch[];
  /** movementUuid → todos los matches que apuntan a ese movimiento (N:M). */
  matchesByMovementUuid: Record<string, ReconciliationMatch[]>;
  /** "jdtNum#lineId" → todos los matches que apuntan a ese apunte (N:M). */
  matchesBySapKey: Record<string, ReconciliationMatch[]>;
  account: {
    iban: string;
    alias: string;
    bankCode: string;
    bankName: string;
    companyId: string;
    currency: string;
    sapGLAccount: string;
    lastSyncedAt: string | null;
    sapEntriesLastSyncedAt: string | null;
    sapEntriesLastError: string | null;
  };
};

export type ReconciliationRunResult = {
  iban: string;
  totalMovements: number;
  totalSapEntries: number;
  totalMatches: number;
  autoMatchesBefore: number;
  autoMatchesAfter: number;
  manualMatches: number;
  newAutoMatches: number;
  droppedAutoMatches: number;
  pendingMovements: number;
  pendingSapEntries: number;
  balanceSealDate: string | null;
  sealedMovements: number;
  sealedSapEntries: number;
};

export async function fetchAccountReconciliation(iban: string): Promise<ReconciliationSnapshot> {
  const res = await fetch(`/api/accounts/${encodeURIComponent(iban)}/reconciliation`);
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  return res.json() as Promise<ReconciliationSnapshot>;
}

export async function runAccountReconciliation(iban: string): Promise<ReconciliationRunResult> {
  const res = await fetch(`/api/accounts/${encodeURIComponent(iban)}/reconciliation/run`, {
    method: "POST",
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  const json = (await res.json()) as { result: ReconciliationRunResult };
  return json.result;
}

/**
 * Anula el "casado por saldo" (balanceSealDate) de una cuenta. Los matches
 * 1:1 se conservan; sólo se borra la fecha de sello. Devuelve la fecha que
 * estaba activa (por si quieres mostrar "Anulado: era X") o null si no
 * había ninguna.
 */
export async function clearReconciliationSeal(iban: string): Promise<string | null> {
  const res = await fetch(
    `/api/accounts/${encodeURIComponent(iban)}/reconciliation/clear-seal`,
    { method: "POST" },
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  const json = (await res.json()) as { clearedFrom: string | null };
  return json.clearedFrom;
}

export async function createManualReconciliationMatch(
  iban: string,
  match: { movementUuid: string; jdtNum: number; lineId: number },
): Promise<void> {
  const res = await fetch(`/api/accounts/${encodeURIComponent(iban)}/reconciliation/match`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(match),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
}

/**
 * Deshace un emparejamiento manual.
 * - Sin `link`: borra TODOS los enlaces del movimiento.
 * - Con `link` (jdtNum + lineId): borra solo ese enlace específico (para N:M).
 */
export async function deleteReconciliationMatch(
  iban: string,
  movementUuid: string,
  link?: { jdtNum: number; lineId: number },
): Promise<void> {
  const qs = link ? `?jdtNum=${link.jdtNum}&lineId=${link.lineId}` : "";
  const res = await fetch(
    `/api/accounts/${encodeURIComponent(iban)}/reconciliation/match/${encodeURIComponent(movementUuid)}${qs}`,
    { method: "DELETE" },
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
}

// --- Reglas de contrapartida (contabilización) ---

export type PostingRuleSign = "any" | "debit" | "credit";
export type OpenItemsSource = "invoices" | "purchase_invoices";

export type PostingRuleActionFixed = {
  type: "fixed_account";
  contraAccount: string;
  /** Centro de coste (OPRC). Obligatorio si la cuenta empieza por 6 o 7. */
  costingCode?: string;
  /** Proyecto (OPRJ). Obligatorio junto al CC cuando la cuenta empieza por 6 o 7. */
  projectCode?: string;
};

/** True si el código contable requiere Centro de coste (6* o 7* en PGC ES). */
export function accountRequiresCostingCode(contraAccount: string): boolean {
  const s = (contraAccount ?? "").trim();
  return s.startsWith("6") || s.startsWith("7");
}
export type PostingRuleActionOpenItems = {
  type: "open_items";
  sources: OpenItemsSource[];
  dateWindowDays: number | null;
};
export type PostingRuleActionBills = {
  type: "bills_of_exchange";
  dateWindowDays: number | null;
  accountPayable: string;
  accountReceivable: string;
};
export type PostingRuleActionLeasingInstallment = {
  type: "leasing_installment";
};
export type PostingRuleActionLoanInstallment = {
  type: "loan_installment";
};
export type PostingRuleAction =
  | PostingRuleActionFixed
  | PostingRuleActionOpenItems
  | PostingRuleActionBills
  | PostingRuleActionLeasingInstallment
  | PostingRuleActionLoanInstallment;

/** Constantes para el pseudo-bancode "reglas globales" (aplican a todos los
 *  bancos). En URL usamos el token GLOBAL_BANK_URL_TOKEN por si en el futuro
 *  algún proxy tuviera problemas con el asterisco. */
export const GLOBAL_BANK_CODE = "*";
export const GLOBAL_BANK_URL_TOKEN = "__all__";

export function bankCodeToUrl(bankCode: string): string {
  if (bankCode === GLOBAL_BANK_CODE) return GLOBAL_BANK_URL_TOKEN;
  return encodeURIComponent(bankCode);
}

export type PostingRule = {
  id: string;
  bankCode: string;
  name: string;
  /** Lista de substrings; la regla casa si ALGUNO aparece en el texto del
   *  movimiento. Los backends antiguos podían mandar `pattern` singular; el
   *  store lo normaliza a `patterns: [pattern]` al leer. */
  patterns: string[];
  signFilter: PostingRuleSign;
  amountMin: number | null;
  amountMax: number | null;
  action: PostingRuleAction;
  priority: number;
  /** Si true, la regla también casa cuando el memo del movimiento contiene
   *  los últimos 10 dígitos del contractNumber de algún leasing activo del
   *  mismo bankCode. Útil para leasings cuya constitución/apertura no lleva
   *  el patrón textual habitual. */
  matchLeasingByContract?: boolean;
  /** Si true, la regla también casa cuando el memo contiene el
   *  descriptionPattern configurado en algún préstamo activo del mismo
   *  bankCode, o los últimos 10 dígitos de su contractNumber. Análogo a
   *  matchLeasingByContract. Se combina naturalmente con
   *  action.type = "loan_installment". */
  matchLoanByContract?: boolean;
  /** Si true, la regla también casa cuando el memo contiene el lineNumber
   *  (normalizado a dígitos) de alguna línea de aval activa del mismo
   *  bankCode. Combinado con fixed_account: si además se detecta un aval
   *  concreto, la contraAccount/CC/PJ se sobrescriben con los del aval. */
  matchAvalByLine?: boolean;
  createdAt: string;
  updatedAt: string;
};

export type PostingRuleBank = {
  bankCode: string;
  bankName: string;
  accountsCount: number;
  rulesCount: number;
};

export async function fetchPostingRulesBanks(): Promise<{ count: number; items: PostingRuleBank[] }> {
  const res = await fetch("/api/posting-rules/banks");
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  return res.json() as Promise<{ count: number; items: PostingRuleBank[] }>;
}

export async function fetchPostingRules(
  bankCode: string,
): Promise<{ bankCode: string; count: number; items: PostingRule[] }> {
  const res = await fetch(`/api/posting-rules/banks/${bankCodeToUrl(bankCode)}`);
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  return res.json() as Promise<{ bankCode: string; count: number; items: PostingRule[] }>;
}

export type PostingRuleInput = {
  name: string;
  patterns: string[];
  signFilter?: PostingRuleSign;
  amountMin?: number | null;
  amountMax?: number | null;
  action: PostingRuleAction;
  matchLeasingByContract?: boolean;
  matchLoanByContract?: boolean;
  matchAvalByLine?: boolean;
};

export async function createPostingRule(
  bankCode: string,
  input: PostingRuleInput,
): Promise<PostingRule> {
  const res = await fetch(`/api/posting-rules/banks/${bankCodeToUrl(bankCode)}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  const json = (await res.json()) as { rule: PostingRule };
  return json.rule;
}

export async function updatePostingRule(
  bankCode: string,
  ruleId: string,
  patch: Partial<PostingRuleInput>,
): Promise<PostingRule> {
  const res = await fetch(
    `/api/posting-rules/banks/${bankCodeToUrl(bankCode)}/${encodeURIComponent(ruleId)}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    },
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  const json = (await res.json()) as { rule: PostingRule };
  return json.rule;
}

export async function deletePostingRule(bankCode: string, ruleId: string): Promise<void> {
  const res = await fetch(
    `/api/posting-rules/banks/${bankCodeToUrl(bankCode)}/${encodeURIComponent(ruleId)}`,
    { method: "DELETE" },
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
}

export async function reorderPostingRules(
  bankCode: string,
  orderedIds: string[],
): Promise<PostingRule[]> {
  const res = await fetch(
    `/api/posting-rules/banks/${bankCodeToUrl(bankCode)}/reorder`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ orderedIds }),
    },
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  const json = (await res.json()) as { items: PostingRule[] };
  return json.items;
}

// --- Contabilización de un movimiento ---

export type PostingCandidateInvoice = {
  source: "invoices" | "purchase_invoices";
  docEntry: number;
  docNum: number;
  docDate: string;
  docDueDate: string;
  cardCode: string;
  cardName: string;
  docTotal: number;
  paidToDate: number;
  openAmount: number;
  currency: string;
  reference1: string;
  reference2: string;
  numAtCard: string;
  comments: string;
  score: number;
  matchedBy: Array<"amount" | "name" | "reference">;
};

export type PostingCandidateBoe = {
  docEntry: number;
  docNum: number;
  docDate: string;
  docDueDate: string;
  cardCode: string;
  cardName: string;
  amount: number;
  currency: string;
  reference1: string;
  reference2: string;
  boeType: "payable" | "receivable" | "unknown";
  boeStatus: string;
  score: number;
  matchedBy: Array<"amount" | "name" | "reference">;
};

export type PostingAvalProposal = {
  bankCode: string;
  lineNumber: string;
  lineAlias: string;
  avalNumber: string;
  avalRecipient: string;
  costingCode: string;
  projectCode: string;
  expenseAccount: string;
  rowType: "opening" | "periodic";
  rowIndex: number;
  rowDueDate: string;
  rowCommission: number;
};

export type PostingLeasingProposal = {
  bankCode: string;
  contractNumber: string;
  leasingAlias: string;
  supplierCardCode: string;
  supplierName: string;
  installmentIndex: number;
  installmentDate: string;
  installmentTotal: number;
  /** Modo del asiento:
   *   - "total" (default): factura completa + payment completo + reclas.
   *   - "partial_new": factura completa + payment PARCIAL. La cuota queda
   *     como "partial" con paidAmount = cargo y invoiceDocEntry guardado.
   *   - "residual": SIN factura nueva. Solo payment adicional contra el
   *     invoiceDocEntry ya guardado. Cierra la cuota. Sin reclas. */
  kind: "total" | "partial_new" | "residual" | "partial_add";
  /** Solo aplica en kind="residual" — importe pagado en parciales previos. */
  paidBefore: number;
  /** Solo aplica en kind="residual" — DocEntry de la factura ya emitida. */
  invoiceDocEntry: number | null;
  invoiceLines: Array<{
    role: "amortized" | "interest" | "fees";
    accountCode: string;
    lineTotal: number;
    costingCode: string | null;
    projectCode: string | null;
    vatGroup: string | null;
    description: string;
  }>;
  reclass: {
    amount: number;
    lines: Array<{
      role: "reclass_lp" | "reclass_cp";
      accountCode: string;
      debit: number;
      credit: number;
      memo: string;
    }>;
  } | null;
  isConstitution: boolean;
};

/** Propuesta cuota de préstamo (loan_installment): 3-6 líneas del asiento. */
export type PostingLoanProposal = {
  loanBankCode: string;
  loanContractNumber: string;
  loanAlias: string;
  detectedBy: "text" | "amount";
  installmentIndex: number;
  installmentDate: string;
  installmentAmortized: number;
  installmentInterest: number;
  installmentTotal: number;
  reclassificationAmount: number | null;
  suggestedFees: number;
  /** Modo del asiento:
   *   - "total": asiento completo estándar (cuota entera en un solo cargo).
   *   - "partial_new": 2 líneas — Debe puente / Haber banco.
   *   - "residual": asiento completo + cancela cuenta puente por lo pagado antes. */
  kind: "total" | "partial_new" | "residual" | "partial_add";
  /** Solo aplica en kind="residual" — importe pagado en parciales previos. */
  paidBefore: number;
  lines: Array<{
    role:
      | "principal_cp"
      | "interest"
      | "fees"
      | "reclass_lp"
      | "reclass_cp"
      | "bank"
      | "bridge";
    accountCode: string;
    costingCode: string | null;
    debit: number;
    credit: number;
    memo: string;
  }>;
  debitTotal: number;
  creditTotal: number;
};

export type PostingPreview =
  | {
      ok: true;
      iban: string;
      movementUuid: string;
      ruleId: string;
      ruleName: string;
      actionType:
        | "fixed_account"
        | "open_items"
        | "bills_of_exchange"
        | "leasing_installment"
        | "loan_installment";
      glAccount: string;
      invoiceCandidates: PostingCandidateInvoice[];
      boeCandidates: PostingCandidateBoe[];
      leasingProposal: PostingLeasingProposal | null;
      loanProposal: PostingLoanProposal | null;
      avalProposal: PostingAvalProposal | null;
      boeAccountPayable: string | null;
      boeAccountReceivable: string | null;
      contraAccount: string | null;
      costingCode: string | null;
      projectCode: string | null;
      movement: Movement;
      searchTokens: {
        amount: number;
        names: string[];
        references: string[];
      };
    }
  | {
      ok: false;
      iban: string;
      movementUuid: string;
      reason: string;
      message: string;
      leasingResidualReopenCandidates?: LeasingResidualReopenCandidate[];
    };

/** Cuota YA marcada como paid pero cuya diferencia con el importe del
 *  movimiento sugiere que este es un residual sobre una cuota mal cerrada. */
export type LeasingResidualReopenCandidate = {
  bankCode: string;
  contractNumber: string;
  installmentIndex: number;
  installmentDate: string;
  totalPayment: number;
  residualAmount: number;
  suggestedNewPaidAmount: number;
  hasInvoiceDocEntry: boolean;
};

/** Reabre la cuota como partial con paidAmount indicado. Si `invoiceDocEntry`
 *  no viene, se hereda del actual de la cuota (si existe). */
export async function reopenLeasingInstallmentAsPartial(input: {
  bankCode: string;
  contractNumber: string;
  index: number;
  paidAmount: number;
  invoiceDocEntry?: number;
}): Promise<{ ok: true }> {
  const { bankCode, contractNumber, index, ...body } = input;
  const res = await fetch(
    `/api/leasings/${encodeURIComponent(bankCode)}/${encodeURIComponent(contractNumber.trim())}/schedule/installment/${index}/reopen-partial`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  const text = await res.text();
  if (!res.ok) {
    let msg = text;
    try {
      const p = JSON.parse(text) as { message?: string };
      if (p.message) msg = p.message;
    } catch { /* keep */ }
    throw new Error(msg);
  }
  return { ok: true };
}

export async function fetchPostingPreview(
  iban: string,
  movementUuid: string,
): Promise<PostingPreview> {
  const res = await fetch(
    `/api/accounts/${encodeURIComponent(iban)}/movements/${encodeURIComponent(movementUuid)}/posting-preview`,
  );
  const body = (await res.json()) as PostingPreview;
  return body;
}

export type PostingChoice =
  | {
      kind: "invoice";
      source: "invoices" | "purchase_invoices";
      invoices: Array<{ docEntry: number; sumApplied: number }>;
      memo?: string;
    }
  | {
      kind: "on_account";
      cardCode: string;
      cardType: "customer" | "supplier";
      memo?: string;
    }
  | {
      kind: "boe";
      docEntry: number;
      docNum: number;
      boeAccount: string;
      sumApplied: number;
      memo?: string;
    }
  | {
      kind: "fixed_account";
      contraAccount: string;
      costingCode?: string;
      projectCode?: string;
      memo?: string;
      /** Si el preview detectó aval, se envía para que el backend marque
       *  la fila del cuadro como pagada tras crear el JournalEntry. */
      avalRef?: {
        bankCode: string;
        lineNumber: string;
        avalNumber: string;
        rowType: "opening" | "periodic";
        rowIndex: number;
      };
    }
  | {
      kind: "leasing_installment";
      bankCode: string;
      contractNumber: string;
      installmentIndex: number;
      /** total | partial_new | residual — default "total". */
      installmentKind?: "total" | "partial_new" | "residual" | "partial_add";
      memo?: string;
    };

export type PostMovementResult = {
  ok: true;
  iban: string;
  movementUuid: string;
  jdtNum: number;
  docEntry: number | null;
  documentType: "incoming_payment" | "vendor_payment" | "journal_entry";
  postedAt: string;
};

/** Respuesta asíncrona del endpoint post-to-sap: cuando el flow puede tardar
 *  más de 60s (leasing_installment), el backend responde 202 con este payload
 *  y el cliente polea `getLeasingPostingStatus` hasta terminar. */
export type PostMovementAsyncResult = {
  ok: true;
  async: true;
  operationId: string;
};

export async function postMovementToSap(
  iban: string,
  movementUuid: string,
  choice: PostingChoice,
): Promise<PostMovementResult | PostMovementAsyncResult> {
  const res = await fetch(
    `/api/accounts/${encodeURIComponent(iban)}/movements/${encodeURIComponent(movementUuid)}/post-to-sap`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(choice),
    },
  );
  const text = await res.text();
  if (!res.ok) {
    let msg = text;
    try {
      const p = JSON.parse(text) as { message?: string };
      if (p.message) msg = p.message;
    } catch { /* keep raw */ }
    throw new Error(msg);
  }
  return JSON.parse(text) as PostMovementResult | PostMovementAsyncResult;
}

export type LeasingPostingOpStatus =
  | { status: "running"; startedAt: string }
  | { status: "ok"; startedAt: string; finishedAt: string; jdtNum: number; docEntry: number | null }
  | { status: "error"; startedAt: string; finishedAt: string; message: string };

export async function getLeasingPostingStatus(
  iban: string,
  movementUuid: string,
  operationId: string,
): Promise<LeasingPostingOpStatus> {
  const res = await fetch(
    `/api/accounts/${encodeURIComponent(iban)}/movements/${encodeURIComponent(movementUuid)}/leasing-posting-status/${encodeURIComponent(operationId)}`,
  );
  const text = await res.text();
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}: ${text.slice(0, 200)}`);
  }
  return JSON.parse(text) as LeasingPostingOpStatus;
}

/** Borra la marca de "contabilizado" del movimiento LOCAL (no toca SAP).
 *  Uso: cuando el asiento SAP fue anulado a mano y quieres rehacerlo desde
 *  la app. También borra el match de reconciliación si existe. */
export async function clearMovementPosting(
  iban: string,
  movementUuid: string,
): Promise<void> {
  const res = await fetch(
    `/api/accounts/${encodeURIComponent(iban)}/movements/${encodeURIComponent(movementUuid)}/posting`,
    { method: "DELETE" },
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
}

/**
 * Marca un movimiento como YA contabilizado en SAP apuntando a un pago/asiento
 * existente. No crea documento en SAP. Uso: cuando SAP creó el documento pero
 * el flujo automático falló al finalizar el match (típico: JdtNum no venía en
 * la respuesta del SL y no había fallback HANA).
 */
export async function markMovementPostedManual(input: {
  iban: string;
  movementUuid: string;
  documentType: "vendor_payment" | "incoming_payment" | "journal_entry";
  docNum?: number;
  docEntry?: number;
  jdtNum?: number;
  memo?: string;
}): Promise<PostMovementResult> {
  const { iban, movementUuid, ...body } = input;
  const res = await fetch(
    `/api/accounts/${encodeURIComponent(iban)}/movements/${encodeURIComponent(movementUuid)}/posting-manual`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  const text = await res.text();
  if (!res.ok) {
    let msg = text;
    try {
      const p = JSON.parse(text) as { message?: string };
      if (p.message) msg = p.message;
    } catch { /* keep raw */ }
    throw new Error(msg);
  }
  return JSON.parse(text) as PostMovementResult;
}

// ─── Cuota de préstamo: preview + POST ────────────────────────────────────────

export type LoanInstallmentLineProposed = {
  role: "principal_cp" | "interest" | "fees" | "reclass_lp" | "reclass_cp" | "bank";
  accountCode: string;
  costingCode: string | null;
  debit: number;
  credit: number;
  memo: string;
};

export type LoanInstallmentPreview =
  | {
      ok: true;
      iban: string;
      movementUuid: string;
      movement: Movement;
      bankGLAccount: string;
      detectedBy: "text" | "amount";
      loans: Array<{ bankCode: string; contractNumber: string; alias: string }>;
      chosenLoan: { bankCode: string; contractNumber: string; alias: string } | null;
      installmentMatch:
        | {
            ok: true;
            index: number;
            installment: {
              date: string;
              amortized: number;
              interest: number;
              total: number;
              pendingCapital: number;
              operationType: string;
            };
          }
        | { ok: false; reason: "ambiguous"; candidates: unknown[] }
        | { ok: false; reason: "not_found" }
        | { ok: false; reason: "no_amortization" };
      proposed: {
        loanBankCode: string;
        loanContractNumber: string;
        loanAlias: string;
        installmentDate: string;
        installmentAmortized: number;
        installmentInterest: number;
        installmentTotal: number;
        reclassificationAmount: number | null;
        suggestedFees: number;
        lines: LoanInstallmentLineProposed[];
        debitTotal: number;
        creditTotal: number;
      } | null;
    }
  | {
      ok: false;
      iban: string;
      movementUuid: string;
      reason: string;
      message: string;
    };

export async function fetchLoanInstallmentPreview(
  iban: string,
  movementUuid: string,
  loanKey?: string,
): Promise<LoanInstallmentPreview> {
  const qs = loanKey ? `?loanKey=${encodeURIComponent(loanKey)}` : "";
  const res = await fetch(
    `/api/accounts/${encodeURIComponent(iban)}/movements/${encodeURIComponent(movementUuid)}/loan-installment-preview${qs}`,
  );
  return (await res.json()) as LoanInstallmentPreview;
}

export type LoanInstallmentLineInput = {
  accountCode: string;
  costingCode?: string;
  debit: number;
  credit: number;
  memo?: string;
};

// ─── Conceptos recurrentes (previsión de tesorería) ───────────────────────

export type RecurringFrequency = "monthly" | "quarterly" | "annual" | "once";

export type RecurringItem = {
  id: string;
  companyId: string;
  name: string;
  kind: "inflow" | "outflow";
  amount: number;
  currency: string;
  frequency: RecurringFrequency;
  dayOfMonth: number | null;
  startDate: string;
  endDate: string | null;
  accountIban: string | null;
  notes: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
};

export async function fetchRecurringItems(
  companyId?: string,
): Promise<{ count: number; items: RecurringItem[] }> {
  const qs = companyId ? `?companyId=${encodeURIComponent(companyId)}` : "";
  const res = await fetch(`/api/recurring-items${qs}`);
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  return res.json() as Promise<{ count: number; items: RecurringItem[] }>;
}

export type RecurringItemInput = {
  companyId: string;
  name: string;
  kind: "inflow" | "outflow";
  amount: number;
  currency?: string;
  frequency: RecurringFrequency;
  dayOfMonth?: number | null;
  startDate: string;
  endDate?: string | null;
  accountIban?: string | null;
  notes?: string;
  active?: boolean;
};

export async function createRecurringItem(input: RecurringItemInput): Promise<RecurringItem> {
  const res = await fetch("/api/recurring-items", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  const json = (await res.json()) as { item: RecurringItem };
  return json.item;
}

export async function patchRecurringItem(
  id: string,
  patch: Partial<RecurringItemInput>,
): Promise<RecurringItem> {
  const res = await fetch(`/api/recurring-items/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  const json = (await res.json()) as { item: RecurringItem };
  return json.item;
}

export async function deleteRecurringItem(id: string): Promise<void> {
  const res = await fetch(`/api/recurring-items/${encodeURIComponent(id)}`, { method: "DELETE" });
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
}

// ─── Previsión de tesorería ────────────────────────────────────────────────

export type ForecastGranularity = "week" | "month";
export type ForecastScenario = "realistic" | "pessimistic" | "optimistic";

export type ForecastShifts = {
  receivableDelayDays: number;
  payableDelayDays: number;
};

export type ForecastPeriod = {
  from: string;
  to: string;
  label: string;
  openingBalance: number;
  inflows: { invoices: number; boe: number; recurring: number; total: number };
  outflows: {
    invoices: number;
    boe: number;
    loans: number;
    leasings: number;
    avales: number;
    recurring: number;
    total: number;
  };
  closingBalance: number;
};

export type ForecastBpDelaysInfo = {
  used: boolean;
  computedAt: string | null;
  fromDate: string | null;
  toDate: string | null;
  customerBpsWithHistory: number;
  supplierBpsWithHistory: number;
};

export type ForecastResult = {
  companyId: string;
  currency: string;
  generatedAt: string;
  horizonMonths: number;
  granularity: ForecastGranularity;
  scenario: ForecastScenario;
  shifts: ForecastShifts;
  bpDelays: ForecastBpDelaysInfo;
  startingBalance: number;
  periods: ForecastPeriod[];
  warnings: string[];
};

export type ForecastQuery = {
  months?: number;
  granularity?: ForecastGranularity;
  currency?: string;
  scenario?: ForecastScenario;
  receivableDelay?: number;
  payableDelay?: number;
  /** false para desactivar el retraso histórico por BP (default: true). */
  useHistoricalDelays?: boolean;
};

function forecastQueryString(params: ForecastQuery): string {
  const qs = new URLSearchParams();
  if (params.months !== undefined) qs.set("months", String(params.months));
  if (params.granularity) qs.set("granularity", params.granularity);
  if (params.currency) qs.set("currency", params.currency);
  if (params.scenario) qs.set("scenario", params.scenario);
  if (params.receivableDelay !== undefined) qs.set("receivableDelay", String(params.receivableDelay));
  if (params.payableDelay !== undefined) qs.set("payableDelay", String(params.payableDelay));
  if (params.useHistoricalDelays === false) qs.set("useHistoricalDelays", "off");
  return qs.toString();
}

export async function fetchForecast(
  companyId: string,
  params: ForecastQuery = {},
): Promise<ForecastResult> {
  const qs = forecastQueryString(params);
  const url = `/api/companies/${encodeURIComponent(companyId)}/forecast${qs ? `?${qs}` : ""}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  return res.json() as Promise<ForecastResult>;
}

export type ConsolidatedForecastResult = {
  companies: Array<{ id: string; name: string; alias: string }>;
  currency: string;
  generatedAt: string;
  horizonMonths: number;
  granularity: ForecastGranularity;
  scenario: ForecastScenario;
  shifts: ForecastShifts;
  bpDelaysCompaniesUsed: number;
  startingBalance: number;
  periods: ForecastPeriod[];
  warnings: string[];
  failures: Array<{ companyId: string; message: string }>;
};

// ─── Retraso histórico por Business Partner ────────────────────────────────

export type BpBehaviorSide = "customer" | "supplier";

export type BpBehaviorEntry = {
  cardCode: string;
  cardName: string;
  side: BpBehaviorSide;
  medianDelayDays: number;
  samples: number;
};

export type BpPaymentBehavior = {
  companyId: string;
  computedAt: string;
  fromDate: string;
  toDate: string;
  items: BpBehaviorEntry[];
};

export type BpBehaviorRecalcResult = {
  companyId: string;
  computedAt: string;
  fromDate: string;
  toDate: string;
  customerBps: number;
  supplierBps: number;
  invoicesScanned: number;
  purchaseInvoicesScanned: number;
};

export async function fetchBpBehavior(companyId: string): Promise<BpPaymentBehavior | null> {
  const res = await fetch(`/api/companies/${encodeURIComponent(companyId)}/bp-payment-behavior`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  return res.json() as Promise<BpPaymentBehavior>;
}

export async function recalculateBpBehavior(companyId: string): Promise<BpBehaviorRecalcResult> {
  const res = await fetch(
    `/api/companies/${encodeURIComponent(companyId)}/bp-payment-behavior/recalc`,
    { method: "POST" },
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  const json = (await res.json()) as { result: BpBehaviorRecalcResult };
  return json.result;
}

export async function deleteBpBehavior(companyId: string): Promise<void> {
  const res = await fetch(
    `/api/companies/${encodeURIComponent(companyId)}/bp-payment-behavior`,
    { method: "DELETE" },
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
}

export async function fetchConsolidatedForecast(
  params: ForecastQuery & { companyIds?: string[] } = {},
): Promise<ConsolidatedForecastResult> {
  const qs = new URLSearchParams(forecastQueryString(params));
  if (params.companyIds && params.companyIds.length > 0) {
    qs.set("companyIds", params.companyIds.join(","));
  }
  const url = `/api/forecast/consolidated${qs.toString() ? `?${qs}` : ""}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  return res.json() as Promise<ConsolidatedForecastResult>;
}

export async function postLoanInstallmentToSap(input: {
  iban: string;
  movementUuid: string;
  loanBankCode: string;
  loanContractNumber: string;
  lines: LoanInstallmentLineInput[];
  memo?: string;
  /** Tipo del asiento (viene del preview). Determina cómo marcar la cuota
   *  tras el POST: partial_new suma al paidAmount; total/residual la cierran. */
  kind?: "total" | "partial_new" | "residual" | "partial_add";
  /** Fecha yyyy-mm-dd de la cuota afectada. Necesaria para localizarla en
   *  el cuadro de amortización al marcarla. */
  installmentDate?: string;
}): Promise<PostMovementResult> {
  const { iban, movementUuid, ...body } = input;
  const res = await fetch(
    `/api/accounts/${encodeURIComponent(iban)}/movements/${encodeURIComponent(movementUuid)}/post-loan-installment`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  const text = await res.text();
  if (!res.ok) {
    let msg = text;
    try {
      const p = JSON.parse(text) as { message?: string };
      if (p.message) msg = p.message;
    } catch { /* keep raw */ }
    throw new Error(msg);
  }
  return JSON.parse(text) as PostMovementResult;
}

// --- Avales ---

export type AvalPeriodicity = "monthly" | "quarterly" | "semiannual" | "annual";
export type AvalStatus = "Vigente" | "Cancelado" | "Ejecutado" | "Vencido";

export type AvalLine = {
  bankCode: string;
  lineNumber: string;
  companyId: string;
  alias: string;
  bankName: string;
  iban: string;
  currency: string;
  notes: string;
  active: boolean;
  /** Importe total contratado de la línea (límite de avales vivos). */
  lineAmount: number | null;
  defaultOpeningRatePercent: number | null;
  defaultCommissionRateAnnual: number | null;
  defaultPeriodicity: AvalPeriodicity;
  defaultCommissionMinimum: number | null;
  /** Cuenta contable SAP donde se imputa el gasto de las comisiones de los
   *  avales de esta línea. Vacía = pendiente de configurar; sin ella no se
   *  puede contabilizar automáticamente. */
  sapAccountExpense: string;
  createdAt: string;
  updatedAt: string;
};

export type AvalLineStats = {
  count: number;
  countVigente: number;
  sumTotal: number;
  sumVigente: number;
};

export type AvalLineWithStats = AvalLine & { stats: AvalLineStats };

export type Aval = {
  lineKey: string;
  avalNumber: string;
  amount: number;
  currency: string;
  constitutionDate: string | null;
  possibleMaturityDate: string | null;
  reason: string;
  recipient: string;
  recipientCif: string;
  costingCode: string;
  projectCode: string;
  status: AvalStatus;
  openingRatePercent: number | null;
  commissionRateAnnual: number | null;
  periodicity: AvalPeriodicity;
  commissionMinimum: number | null;
  notes: string;
  createdAt: string;
  updatedAt: string;
};

export type AvalScheduleRow = {
  index: number;
  type: "opening" | "periodic";
  dueDate: string;
  basisAmount: number;
  ratePercent: number;
  commission: number;
  paidAt: string | null;
  paidJdtNum: number | null;
  /** PROVISIONAL fase implantación: true si el usuario lo marcó a mano. */
  manuallyMarked?: boolean;
};

export type AvalScheduleResponse = {
  aval: Aval;
  rows: AvalScheduleRow[];
  generatedAt: string | null;
  horizonUsed: string | null;
  defaultHorizon: string;
};

export async function fetchAvalLines(): Promise<{ count: number; items: AvalLineWithStats[] }> {
  const res = await fetch("/api/avals/lines");
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json() as Promise<{ count: number; items: AvalLineWithStats[] }>;
}

async function jsonOrThrow<T>(res: Response): Promise<T> {
  const text = await res.text();
  if (!res.ok) {
    let msg = text;
    try {
      const p = JSON.parse(text) as { message?: string };
      if (p.message) msg = p.message;
    } catch { /* keep */ }
    throw new Error(msg);
  }
  return JSON.parse(text) as T;
}

export async function createAvalLineApi(input: {
  bankCode: string;
  lineNumber: string;
  companyId: string;
  alias?: string;
  bankName?: string;
  iban?: string;
  currency?: string;
  notes?: string;
  lineAmount?: number | null;
  defaultOpeningRatePercent?: number | null;
  defaultCommissionRateAnnual?: number | null;
  defaultPeriodicity?: AvalPeriodicity;
  defaultCommissionMinimum?: number | null;
  sapAccountExpense?: string;
}): Promise<AvalLine> {
  const res = await fetch("/api/avals/lines", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  return (await jsonOrThrow<{ line: AvalLine }>(res)).line;
}

export async function patchAvalLine(
  bankCode: string,
  lineNumber: string,
  patch: Partial<Omit<AvalLine, "bankCode" | "lineNumber" | "createdAt" | "updatedAt">>,
): Promise<AvalLine> {
  const res = await fetch(
    `/api/avals/lines/${encodeURIComponent(bankCode)}/${encodeURIComponent(lineNumber)}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    },
  );
  return (await jsonOrThrow<{ line: AvalLine }>(res)).line;
}

export async function deleteAvalLineApi(bankCode: string, lineNumber: string): Promise<void> {
  const res = await fetch(
    `/api/avals/lines/${encodeURIComponent(bankCode)}/${encodeURIComponent(lineNumber)}`,
    { method: "DELETE" },
  );
  if (!res.ok) {
    const text = await res.text();
    try {
      const p = JSON.parse(text) as { message?: string };
      if (p.message) throw new Error(p.message);
    } catch { /* fallthrough */ }
    throw new Error(`HTTP ${res.status}`);
  }
}

export async function fetchAvalsByLine(
  bankCode: string,
  lineNumber: string,
): Promise<{ line: AvalLine; count: number; avals: Aval[] }> {
  const res = await fetch(
    `/api/avals/lines/${encodeURIComponent(bankCode)}/${encodeURIComponent(lineNumber)}/avals`,
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json() as Promise<{ line: AvalLine; count: number; avals: Aval[] }>;
}

export async function createAvalApi(input: {
  bankCode: string;
  lineNumber: string;
  avalNumber: string;
  amount: number;
  currency?: string;
  constitutionDate?: string | null;
  possibleMaturityDate?: string | null;
  reason?: string;
  recipient?: string;
  recipientCif?: string;
  costingCode?: string;
  projectCode?: string;
  status?: AvalStatus;
  openingRatePercent?: number | null;
  commissionRateAnnual?: number | null;
  periodicity?: AvalPeriodicity;
  commissionMinimum?: number | null;
  notes?: string;
}): Promise<Aval> {
  const { bankCode, lineNumber, ...body } = input;
  const res = await fetch(
    `/api/avals/lines/${encodeURIComponent(bankCode)}/${encodeURIComponent(lineNumber)}/avals`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  return (await jsonOrThrow<{ aval: Aval }>(res)).aval;
}

export async function patchAval(
  bankCode: string,
  lineNumber: string,
  avalNumber: string,
  patch: Partial<Omit<Aval, "lineKey" | "avalNumber" | "createdAt" | "updatedAt">>,
): Promise<Aval> {
  const res = await fetch(
    `/api/avals/lines/${encodeURIComponent(bankCode)}/${encodeURIComponent(lineNumber)}/avals/${encodeURIComponent(avalNumber)}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    },
  );
  return (await jsonOrThrow<{ aval: Aval }>(res)).aval;
}

export async function deleteAvalApi(
  bankCode: string,
  lineNumber: string,
  avalNumber: string,
): Promise<void> {
  const res = await fetch(
    `/api/avals/lines/${encodeURIComponent(bankCode)}/${encodeURIComponent(lineNumber)}/avals/${encodeURIComponent(avalNumber)}`,
    { method: "DELETE" },
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

/**
 * Renombra el nº de aval. Mueve también el schedule asociado en backend.
 * Falla con 409 si el nuevo número ya existe en la misma línea.
 */
export async function renameAvalApi(
  bankCode: string,
  lineNumber: string,
  oldAvalNumber: string,
  newAvalNumber: string,
): Promise<Aval> {
  const res = await fetch(
    `/api/avals/lines/${encodeURIComponent(bankCode)}/${encodeURIComponent(lineNumber)}/avals/${encodeURIComponent(oldAvalNumber)}/rename`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ newAvalNumber }),
    },
  );
  const text = await res.text();
  if (!res.ok) {
    let msg = text;
    try {
      const p = JSON.parse(text) as { message?: string };
      if (p.message) msg = p.message;
    } catch { /* keep */ }
    throw new Error(msg);
  }
  const parsed = JSON.parse(text) as { aval: Aval };
  return parsed.aval;
}

export async function deleteAllAvalsOfLineApi(
  bankCode: string,
  lineNumber: string,
): Promise<{ ok: true; deleted: number }> {
  const res = await fetch(
    `/api/avals/lines/${encodeURIComponent(bankCode)}/${encodeURIComponent(lineNumber)}/avals`,
    { method: "DELETE" },
  );
  return jsonOrThrow<{ ok: true; deleted: number }>(res);
}

export async function fetchAvalSchedule(
  bankCode: string,
  lineNumber: string,
  avalNumber: string,
): Promise<AvalScheduleResponse> {
  const res = await fetch(
    `/api/avals/lines/${encodeURIComponent(bankCode)}/${encodeURIComponent(lineNumber)}/avals/${encodeURIComponent(avalNumber)}/schedule`,
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json() as Promise<AvalScheduleResponse>;
}

/**
 * Marca / desmarca manualmente una fila del cuadro de avales (fase
 * implantación). Provisional.
 */
export async function markAvalRowManualApi(
  bankCode: string,
  lineNumber: string,
  avalNumber: string,
  type: string,
  index: number,
  paidAt: string,
): Promise<void> {
  const url =
    `/api/avals/lines/${encodeURIComponent(bankCode)}/${encodeURIComponent(lineNumber)}` +
    `/avals/${encodeURIComponent(avalNumber)}/schedule/rows/${encodeURIComponent(type)}/${encodeURIComponent(String(index))}/mark-manual`;
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ paidAt }),
  });
  if (!res.ok) {
    const text = await res.text();
    let msg = text;
    try {
      const p = JSON.parse(text) as { message?: string };
      if (p.message) msg = p.message;
    } catch { /* keep */ }
    throw new Error(msg);
  }
}

export async function unmarkAvalRowManualApi(
  bankCode: string,
  lineNumber: string,
  avalNumber: string,
  type: string,
  index: number,
): Promise<void> {
  const url =
    `/api/avals/lines/${encodeURIComponent(bankCode)}/${encodeURIComponent(lineNumber)}` +
    `/avals/${encodeURIComponent(avalNumber)}/schedule/rows/${encodeURIComponent(type)}/${encodeURIComponent(String(index))}/mark-manual`;
  const res = await fetch(url, { method: "DELETE" });
  if (!res.ok) {
    const text = await res.text();
    let msg = text;
    try {
      const p = JSON.parse(text) as { message?: string };
      if (p.message) msg = p.message;
    } catch { /* keep */ }
    throw new Error(msg);
  }
}

export type AvalImportPreviewRow = {
  sourceRow: number;
  avalNumber: string;
  amount: number;
  currency?: string;
  constitutionDate: string | null;
  possibleMaturityDate: string | null;
  recipient?: string;
  recipientCif?: string;
  reason?: string;
  costingCode?: string;
  projectCode?: string;
  status?: AvalStatus;
  openingRatePercent?: number | null;
  commissionRateAnnual?: number | null;
  periodicity?: AvalPeriodicity;
  commissionMinimum?: number | null;
  notes?: string;
};

export type AvalImportPreview = {
  ok: true;
  dryRun: true;
  headersDetected: string[];
  headersRaw: string[];
  headerRowIndex: number;
  rowsCount: number;
  rows: AvalImportPreviewRow[];
  errors: Array<{ row: number; message: string }>;
  fileName: string | null;
};

export type AvalImportResult = {
  ok: true;
  created: number;
  skipped: number;
  updated: number;
  errors: Array<{ row: number; message: string }>;
  headersDetected: string[];
  fileName: string | null;
};

export async function previewAvalsXlsx(
  bankCode: string,
  lineNumber: string,
  file: File,
): Promise<AvalImportPreview> {
  const url = `/api/avals/lines/${encodeURIComponent(bankCode)}/${encodeURIComponent(lineNumber)}/avals/import?dryRun=1`;
  const fd = new FormData();
  fd.append("file", file);
  const res = await fetch(url, { method: "POST", body: fd });
  const text = await res.text();
  if (!res.ok) {
    let msg = text;
    try {
      const p = JSON.parse(text) as { message?: string };
      if (p.message) msg = p.message;
    } catch { /* keep */ }
    throw new Error(msg);
  }
  return JSON.parse(text) as AvalImportPreview;
}

export async function importAvalsXlsx(
  bankCode: string,
  lineNumber: string,
  file: File,
  overwrite: boolean,
): Promise<AvalImportResult> {
  const qs = overwrite ? "?overwrite=1" : "";
  const url = `/api/avals/lines/${encodeURIComponent(bankCode)}/${encodeURIComponent(lineNumber)}/avals/import${qs}`;
  const fd = new FormData();
  fd.append("file", file);
  const res = await fetch(url, { method: "POST", body: fd });
  const text = await res.text();
  if (!res.ok) {
    let msg = text;
    try {
      const p = JSON.parse(text) as { message?: string };
      if (p.message) msg = p.message;
    } catch { /* keep */ }
    throw new Error(msg);
  }
  return JSON.parse(text) as AvalImportResult;
}

export async function regenerateAvalSchedule(
  bankCode: string,
  lineNumber: string,
  avalNumber: string,
): Promise<{ ok: true; rowsCount: number; generatedAt: string; horizonUsed: string }> {
  const res = await fetch(
    `/api/avals/lines/${encodeURIComponent(bankCode)}/${encodeURIComponent(lineNumber)}/avals/${encodeURIComponent(avalNumber)}/schedule/regenerate`,
    { method: "POST" },
  );
  return jsonOrThrow<{ ok: true; rowsCount: number; generatedAt: string; horizonUsed: string }>(res);
}

/**
 * Reintenta la reconciliación interna en SAP para un movimiento cuyo pago
 * ya se creó pero la fase 2 (Reconcile) falló. Uso: pago a cuenta creado
 * OK, factura DocEntry conocido, pero quedó suelto porque SAP rechazó el
 * Reconcile. El backend llama a InternalReconciliationsService_Reconcile
 * con los TransId/lineId resueltos vía HANA.
 */
export async function retryInternalReconciliation(
  iban: string,
  movementUuid: string,
  input: {
    cardCode: string;
    source: "invoices" | "purchase_invoices";
    invoices: Array<{ docEntry: number; amount: number }>;
  },
): Promise<{ ok: true; jdtNum: number; reconciledRows: number }> {
  const res = await fetch(
    `/api/accounts/${encodeURIComponent(iban)}/movements/${encodeURIComponent(movementUuid)}/retry-reconcile`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    },
  );
  return jsonOrThrow<{ ok: true; jdtNum: number; reconciledRows: number }>(res);
}

export type OrphanBoeItem = {
  boeKey: number;
  boeNum: number;
  boeStatus: string;
  boeType: string;
  cardCode: string;
  cardName: string;
  dueDate: string;
  boeSum: number;
  /** true si además de estar abierto en OBOE, la app detectó su pago en el
   *  memo de un posting local ("BOE NNN"). Estos son casi seguro huérfanos. */
  autoDetected: boolean;
  postedIn: {
    iban: string;
    movementUuid: string;
    jdtNum: number;
    opDate: string;
    amount: number;
  } | null;
};

/**
 * Devuelve los efectos "huérfanos" de una empresa: los que están abiertos
 * en OBOE (SAP) pero cuyo pago ya se contabilizó en la app (detectados
 * por el patrón 'BOE NNN' en el memo del movimiento).
 */
export type OrphanBoesDebug = {
  companyId: string;
  accountsChecked: number;
  postingsWithBoeCount: number;
  postingsWithBoeSample: Array<{
    boeNum: number;
    iban: string;
    opDate: string;
    amount: number;
    memo: string;
  }>;
  openBoesMatchedByNum: number;
  journalEntryPostingsSample: Array<{
    iban: string;
    opDate: string;
    amount: number;
    memo: string;
  }>;
};

export async function fetchOrphanBoes(
  companyId: string,
): Promise<{ companyId: string; count: number; items: OrphanBoeItem[]; debug?: OrphanBoesDebug }> {
  const res = await fetch(`/api/sap/companies/${encodeURIComponent(companyId)}/orphan-boes`);
  return jsonOrThrow<{ companyId: string; count: number; items: OrphanBoeItem[]; debug?: OrphanBoesDebug }>(res);
}

/**
 * Marca en batch los efectos indicados como Pagados en OBOE (PATCH). El
 * backend intenta uno por uno y devuelve los que fueron OK y los que
 * fallaron con su mensaje.
 */
export async function markOrphanBoesPaid(
  companyId: string,
  boeKeys: number[],
  paidDate?: string,
): Promise<{ ok: number[]; failed: Array<{ boeKey: number; message: string }>; paidDate: string }> {
  const res = await fetch(
    `/api/sap/companies/${encodeURIComponent(companyId)}/orphan-boes/mark-paid`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ boeKeys, ...(paidDate ? { paidDate } : {}) }),
    },
  );
  return jsonOrThrow<{ ok: number[]; failed: Array<{ boeKey: number; message: string }>; paidDate: string }>(res);
}

// --- Pagos a proveedor (SEPA + contabilización) ---

export type SupplierOpenInvoiceRow = {
  DocEntry: number;
  DocNum: number;
  CardCode: string;
  CardName: string;
  DocDate: string;
  DocDueDate: string;
  DocTotal: number;
  PaidToDate: number;
  PendingAmount: number;
  Currency: string;
  Reference1: string;
  Reference2: string;
  NumAtCard: string;
  Comments: string;
  BpIban: string;
  BpLegalName: string;
  /** Método de pago de la factura (OPCH.PeyMethod). El backend devuelve
   *  todas las facturas sin filtrar; la vista aplica chips por método para
   *  que el usuario excluya domiciliaciones u otros métodos que no van por
   *  transferencia. Por defecto los métodos que contienen "domicil" salen
   *  desactivados. */
  PayMethod: string;
  // ── Datos adicionales del proveedor (para confirming XLS Santander) ──
  BpTaxId: string;
  BpAddress: string;
  BpAddressNumber: string;
  BpZipCode: string;
  BpCity: string;
  BpCountry: string;
  BpPhone: string;
  BpFax: string;
  BpEmail: string;
};

export async function fetchOpenSupplierInvoicesForPayment(
  companyId: string,
  toDueDate?: string,
): Promise<{ count: number; items: SupplierOpenInvoiceRow[] }> {
  const qs = new URLSearchParams({ companyId });
  if (toDueDate) qs.set("toDueDate", toDueDate);
  const res = await fetch(`/api/payment-batches/open-invoices?${qs.toString()}`);
  return jsonOrThrow<{ count: number; items: SupplierOpenInvoiceRow[] }>(res);
}

export type PaymentBatchLineStatus = "pending" | "posted" | "failed";
export type PaymentBatchStatus = "generated" | "partially_posted" | "posted" | "cancelled";

export type PaymentBatchLine = {
  id: string;
  invoiceDocEntry: number;
  invoiceDocNum: number;
  invoiceDocDate: string;
  invoiceDueDate: string;
  invoiceNumAtCard: string;
  cardCode: string;
  cardName: string;
  bpLegalName: string;
  creditorIban: string;
  amount: number;
  remittanceInfo: string;
  endToEndId: string;
  status: PaymentBatchLineStatus;
  sapDocEntry: number | null;
  sapDocNum: number | null;
  sapJdtNum: number | null;
  postedAt: string | null;
  lastError: string | null;
};

export type PaymentBatch = {
  id: string;
  companyId: string;
  companyName: string;
  senderIban: string;
  senderAccountAlias: string;
  senderBic: string;
  senderGLAccount: string;
  requestedExecutionDate: string;
  filename: string;
  totalAmount: number;
  currency: string;
  sepaMessageId: string;
  status: PaymentBatchStatus;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  lines: PaymentBatchLine[];
};

export type CreatePaymentBatchInput = {
  companyId: string;
  senderIban: string;
  requestedExecutionDate: string;
  lines: Array<{
    invoiceDocEntry: number;
    amount: number;
    remittanceInfo?: string;
  }>;
};

export async function createPaymentBatch(input: CreatePaymentBatchInput): Promise<{ ok: true; batch: PaymentBatch }> {
  const res = await fetch("/api/payment-batches", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  return jsonOrThrow<{ ok: true; batch: PaymentBatch }>(res);
}

export async function fetchPaymentBatches(companyId?: string): Promise<{ count: number; items: PaymentBatch[] }> {
  const qs = companyId ? `?companyId=${encodeURIComponent(companyId)}` : "";
  const res = await fetch(`/api/payment-batches${qs}`);
  return jsonOrThrow<{ count: number; items: PaymentBatch[] }>(res);
}

export async function fetchPaymentBatchDetail(id: string): Promise<PaymentBatch> {
  const res = await fetch(`/api/payment-batches/${encodeURIComponent(id)}`);
  return jsonOrThrow<PaymentBatch>(res);
}

/** URL absoluta del XML — para usar en un <a href> o para descarga programática. */
export function paymentBatchXmlUrl(id: string, opts: { includePosted?: boolean } = {}): string {
  const qs = opts.includePosted ? "?includePosted=1" : "";
  return `/api/payment-batches/${encodeURIComponent(id)}/xml${qs}`;
}

export type PostPaymentBatchResponse = {
  ok: boolean;
  okCount: number;
  failCount: number;
  results: Array<{
    lineId: string;
    invoiceDocNum: number;
    ok: boolean;
    sapDocEntry?: number;
    sapJdtNum?: number;
    error?: string;
  }>;
  batch: PaymentBatch;
};

export async function postPaymentBatch(id: string, lineIds?: string[]): Promise<PostPaymentBatchResponse> {
  const res = await fetch(`/api/payment-batches/${encodeURIComponent(id)}/post`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(lineIds ? { lineIds } : {}),
  });
  return jsonOrThrow<PostPaymentBatchResponse>(res);
}

export async function cancelPaymentBatchApi(id: string): Promise<{ ok: true; batch: PaymentBatch }> {
  const res = await fetch(`/api/payment-batches/${encodeURIComponent(id)}/cancel`, { method: "POST" });
  return jsonOrThrow<{ ok: true; batch: PaymentBatch }>(res);
}

export async function deletePaymentBatchApi(id: string): Promise<void> {
  const res = await fetch(`/api/payment-batches/${encodeURIComponent(id)}`, { method: "DELETE" });
  await jsonOrThrow<{ ok: true }>(res);
}

// --- Confirming (efectos a pagar) ---

export type ConfirmingBatchLineStatus = "pending" | "posted" | "paid" | "failed";
export type ConfirmingBatchStatus =
  | "generated"
  | "partially_posted"
  | "posted"
  | "settled"
  | "cancelled";

export type ConfirmingBatchLine = {
  id: string;
  invoiceDocEntry: number;
  invoiceDocNum: number;
  invoiceDocDate: string;
  invoiceDueDate: string;
  invoiceNumAtCard: string;
  cardCode: string;
  cardName: string;
  bpLegalName: string;
  creditorIban: string;
  amount: number;
  remittanceInfo: string;
  endToEndId: string;
  /** Fecha de vencimiento del EFECTO (editable). */
  dueDate: string;
  /** Método de pago de la factura (OPCH.PeyMethod). Se usa como
   *  PaymentMethodCode al contabilizar el efecto. */
  payMethod: string;
  // ── Datos completos del proveedor (snapshot) ──
  bpTaxId: string;
  bpAddress: string;
  bpAddressNumber: string;
  bpZipCode: string;
  bpCity: string;
  bpCountry: string;
  bpPhone: string;
  bpFax: string;
  bpEmail: string;
  status: ConfirmingBatchLineStatus;
  sapBoeKey: number | null;
  sapBoeNum: number | null;
  sapPostJdtNum: number | null;
  postedAt: string | null;
  sapPaidJdtNum: number | null;
  paidFromIban: string | null;
  paidAt: string | null;
  lastError: string | null;
};

export type ConfirmingBatch = {
  id: string;
  companyId: string;
  companyName: string;
  senderIban: string;
  senderAccountAlias: string;
  senderBic: string;
  senderGLAccount: string;
  filename: string;
  totalAmount: number;
  currency: string;
  presentationId: string;
  status: ConfirmingBatchStatus;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  lines: ConfirmingBatchLine[];
};

export type CreateConfirmingBatchInput = {
  companyId: string;
  senderIban: string;
  lines: Array<{
    invoiceDocEntry: number;
    amount: number;
    dueDate: string;
    remittanceInfo?: string;
  }>;
};

export async function fetchOpenSupplierInvoicesForConfirming(
  companyId: string,
): Promise<{ count: number; items: SupplierOpenInvoiceRow[] }> {
  const qs = new URLSearchParams({ companyId });
  const res = await fetch(`/api/confirming-batches/open-invoices?${qs.toString()}`);
  return jsonOrThrow<{ count: number; items: SupplierOpenInvoiceRow[] }>(res);
}

export async function createConfirmingBatch(input: CreateConfirmingBatchInput): Promise<{ ok: true; batch: ConfirmingBatch }> {
  const res = await fetch("/api/confirming-batches", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  return jsonOrThrow<{ ok: true; batch: ConfirmingBatch }>(res);
}

export async function fetchConfirmingBatches(companyId?: string): Promise<{ count: number; items: ConfirmingBatch[] }> {
  const qs = companyId ? `?companyId=${encodeURIComponent(companyId)}` : "";
  const res = await fetch(`/api/confirming-batches${qs}`);
  return jsonOrThrow<{ count: number; items: ConfirmingBatch[] }>(res);
}

export function confirmingBatchFileUrl(
  id: string,
  format: "xls" | "csb68" | "sepa" = "xls",
): string {
  const qs =
    format === "sepa" ? "?format=sepa" : format === "csb68" ? "?format=csb68" : "";
  return `/api/confirming-batches/${encodeURIComponent(id)}/file${qs}`;
}

export type PostConfirmingBatchResponse = {
  ok: boolean;
  okCount: number;
  failCount: number;
  results: Array<{
    lineId: string;
    invoiceDocNum: number;
    ok: boolean;
    sapBoeKey?: number;
    sapBoeNum?: number;
    error?: string;
  }>;
  batch: ConfirmingBatch;
};

export async function postConfirmingBatch(id: string, lineIds?: string[]): Promise<PostConfirmingBatchResponse> {
  const res = await fetch(`/api/confirming-batches/${encodeURIComponent(id)}/post`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(lineIds ? { lineIds } : {}),
  });
  return jsonOrThrow<PostConfirmingBatchResponse>(res);
}

export type SettleConfirmingBatchResponse = {
  ok: boolean;
  okCount: number;
  failCount: number;
  results: Array<{
    lineId: string;
    invoiceDocNum: number;
    ok: boolean;
    sapPaidJdtNum?: number;
    error?: string;
  }>;
  batch: ConfirmingBatch;
};

export async function settleConfirmingBatch(
  id: string,
  input: { lineIds: string[]; paidFromIban?: string; paidDate?: string },
): Promise<SettleConfirmingBatchResponse> {
  const res = await fetch(`/api/confirming-batches/${encodeURIComponent(id)}/settle`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  return jsonOrThrow<SettleConfirmingBatchResponse>(res);
}

export async function cancelConfirmingBatchApi(id: string): Promise<{ ok: true; batch: ConfirmingBatch }> {
  const res = await fetch(`/api/confirming-batches/${encodeURIComponent(id)}/cancel`, { method: "POST" });
  return jsonOrThrow<{ ok: true; batch: ConfirmingBatch }>(res);
}

export async function deleteConfirmingBatchApi(id: string): Promise<void> {
  const res = await fetch(`/api/confirming-batches/${encodeURIComponent(id)}`, { method: "DELETE" });
  await jsonOrThrow<{ ok: true }>(res);
}

// ── Calendario de vencimientos (agregado cross-batch) ──

export type ConfirmingDueLine = {
  /** "app" = efecto creado desde un batch de la aplicación (tiene batch
   *  y línea asociados). "sap" = efecto que ya existía en OBOE (creado
   *  a mano en SAP o por otra vía). */
  origin: "app" | "sap";
  sapBoeKey: number;
  sapBoeNum: number;
  cardCode: string;
  cardName: string;
  amount: number;
  /** OPYM.PayMethCod con el que se creó el efecto (P-Confirming,
   *  P-Pagaré, etc.). Vacío si el efecto se creó sin método. */
  paymentMethod: string;
  /** true si el banco emisor viene de una asignación manual (típico
   *  para pagarés): el usuario eligió qué cuenta cargaría el efecto. */
  hasManualAssignment: boolean;
  /** IBAN asignado manualmente (si hasManualAssignment=true). */
  assignedIban?: string;
  /** Fuente del `senderIban` del grupo al que pertenece esta línea:
   *    "batch"  — viene del fichero de la app (no editable)
   *    "manual" — asignación explícita del usuario (editable)
   *    "opym"   — auto-detectado por OPYM.DflIBAN del método
   *    "none"   — sin banco conocido; el usuario debe elegir cta */
  bankSource: "batch" | "manual" | "opym" | "none";
  // Solo cuando origin === "app":
  batchId?: string;
  batchFilename?: string;
  lineId?: string;
  invoiceDocNum?: number;
  invoiceNumAtCard?: string;
};

export async function assignConfirmingBoeAccount(input: {
  companyId: string;
  boeKey: number;
  iban: string;
}): Promise<{ ok: true; assignment: { boeKey: number; iban: string } }> {
  const res = await fetch("/api/confirming-batches/assign-account", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  return jsonOrThrow<{ ok: true; assignment: { boeKey: number; iban: string } }>(res);
}

export async function clearConfirmingBoeAccount(input: {
  companyId: string;
  boeKey: number;
}): Promise<{ ok: true; removed: boolean }> {
  const res = await fetch(
    `/api/confirming-batches/assign-account/${input.boeKey}?companyId=${encodeURIComponent(input.companyId)}`,
    { method: "DELETE" },
  );
  return jsonOrThrow<{ ok: true; removed: boolean }>(res);
}

export type ConfirmingDueGroup = {
  dueDate: string;
  /** Método de pago del grupo (P-Confirming, P-Pagaré, "(sin método)"). */
  paymentMethod: string;
  /** IBAN de la cuenta emisora conocida (deducida de batches). null si
   *  ningún efecto del grupo viene de un batch de la app y el usuario
   *  debe elegir cuenta al pagar. */
  senderIban: string | null;
  /** Alias de la cuenta emisora (para mostrar en UI). null si desconocido. */
  senderAccountAlias: string | null;
  totalAmount: number;
  lineCount: number;
  appCount: number;
  sapCount: number;
  /** BoeKey de los efectos del grupo — el frontend los pasa al endpoint
   *  settle-by-keys para liquidar exactamente estos. */
  boeKeys: number[];
  lines: ConfirmingDueLine[];
};

export async function fetchConfirmingDuesCalendar(
  companyId: string,
): Promise<{ count: number; items: ConfirmingDueGroup[] }> {
  const res = await fetch(
    `/api/confirming-batches/dues-calendar?companyId=${encodeURIComponent(companyId)}`,
  );
  return jsonOrThrow<{ count: number; items: ConfirmingDueGroup[] }>(res);
}

export type SettleByKeysResponse = {
  ok: boolean;
  requestedBoeKeys: number[];
  processedBoeKeys: number[];
  paidDate: string;
  paidFromIban: string;
  sapJdtNum: number;
  totalAmount: number;
  lineCount: number;
  appCount: number;
  sapCount: number;
  results: Array<{
    origin: "app" | "sap";
    sapBoeKey: number;
    sapBoeNum: number;
    cardCode: string;
    amount: number;
    batchId?: string;
    lineId?: string;
    invoiceDocNum?: number;
    ok: boolean;
    warning?: string;
  }>;
};

export async function settleConfirmingByKeys(input: {
  companyId: string;
  boeKeys: number[];
  paidFromIban: string;
  paidDate?: string;
}): Promise<SettleByKeysResponse> {
  const res = await fetch("/api/confirming-batches/settle-by-keys", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  return jsonOrThrow<SettleByKeysResponse>(res);
}

// --- Cobros por domiciliación (adeudo directo SEPA CORE/B2B) ---

export type HanaOpenCustomerInvoiceRow = {
  DocEntry: number;
  DocNum: number;
  CardCode: string;
  CardName: string;
  DocDate: string;
  DocDueDate: string;
  DocTotal: number;
  PaidToDate: number;
  PendingAmount: number;
  Currency: string;
  NumAtCard: string;
  BpIban: string;
  BpTaxId: string;
  BpLegalName: string;
  BpAddress: string;
  BpZipCode: string;
  BpCity: string;
  BpCountry: string;
  BpEmail: string;
  PayMethod: string;
  /** "CORE" | "B2B" — modalidad del adeudo directo del método de pago. */
  DirectDbt: string;
  /** Banco propio del método (OPYM.DflIBAN) — cta que recibirá el abono. */
  OpymDflIban: string;
};

export async function fetchOpenCustomerInvoicesForDD(
  companyId: string,
): Promise<{ count: number; items: HanaOpenCustomerInvoiceRow[] }> {
  const qs = new URLSearchParams({ companyId });
  const res = await fetch(`/api/direct-debit-batches/open-invoices?${qs.toString()}`);
  return jsonOrThrow<{ count: number; items: HanaOpenCustomerInvoiceRow[] }>(res);
}

export type DirectDebitBatchLineStatus = "pending" | "posted" | "failed";
export type DirectDebitBatchStatus = "generated" | "partially_posted" | "posted" | "cancelled";
export type DirectDebitScheme = "CORE" | "B2B";

export type DirectDebitBatchLine = {
  id: string;
  invoiceDocEntry: number;
  invoiceDocNum: number;
  invoiceDocDate: string;
  invoiceDueDate: string;
  invoiceNumAtCard: string;
  cardCode: string;
  cardName: string;
  bpLegalName: string;
  bpTaxId: string;
  debtorIban: string;
  payMethod: string;
  amount: number;
  remittanceInfo: string;
  endToEndId: string;
  mandateId: string;
  mandateSignatureDate: string;
  status: DirectDebitBatchLineStatus;
  sapBoeKey: number | null;
  sapBoeNum: number | null;
  sapPostJdtNum: number | null;
  postedAt: string | null;
  lastError: string | null;
};

export type DirectDebitBatch = {
  id: string;
  companyId: string;
  companyName: string;
  scheme: DirectDebitScheme;
  creditorIban: string;
  creditorAccountAlias: string;
  creditorBic: string;
  creditorGLAccount: string;
  creditorSchemeId: string;
  requestedCollectionDate: string;
  filename: string;
  totalAmount: number;
  currency: string;
  sepaMessageId: string;
  status: DirectDebitBatchStatus;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  lines: DirectDebitBatchLine[];
};

export type CreateDirectDebitBatchInput = {
  companyId: string;
  scheme: DirectDebitScheme;
  requestedCollectionDate: string;
  lines: Array<{
    invoiceDocEntry: number;
    amount: number;
    remittanceInfo?: string;
  }>;
};

export async function createDirectDebitBatch(
  input: CreateDirectDebitBatchInput,
): Promise<{ ok: true; batch: DirectDebitBatch }> {
  const res = await fetch("/api/direct-debit-batches", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  return jsonOrThrow<{ ok: true; batch: DirectDebitBatch }>(res);
}

export async function fetchDirectDebitBatches(
  companyId?: string,
): Promise<{ count: number; items: DirectDebitBatch[] }> {
  const qs = companyId ? `?companyId=${encodeURIComponent(companyId)}` : "";
  const res = await fetch(`/api/direct-debit-batches${qs}`);
  return jsonOrThrow<{ count: number; items: DirectDebitBatch[] }>(res);
}

export async function fetchDirectDebitBatchDetail(id: string): Promise<DirectDebitBatch> {
  const res = await fetch(`/api/direct-debit-batches/${encodeURIComponent(id)}`);
  return jsonOrThrow<DirectDebitBatch>(res);
}

export function directDebitBatchFileUrl(id: string): string {
  return `/api/direct-debit-batches/${encodeURIComponent(id)}/file`;
}

export type PostDirectDebitBatchResponse = {
  ok: boolean;
  okCount: number;
  failCount: number;
  results: Array<{
    lineId: string;
    invoiceDocNum: number;
    ok: boolean;
    sapBoeKey?: number;
    error?: string;
  }>;
  batch: DirectDebitBatch;
};

export async function postDirectDebitBatch(
  id: string,
  lineIds?: string[],
): Promise<PostDirectDebitBatchResponse> {
  const res = await fetch(`/api/direct-debit-batches/${encodeURIComponent(id)}/post`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(lineIds ? { lineIds } : {}),
  });
  return jsonOrThrow<PostDirectDebitBatchResponse>(res);
}

export async function cancelDirectDebitBatchApi(id: string): Promise<{ ok: true; batch: DirectDebitBatch }> {
  const res = await fetch(`/api/direct-debit-batches/${encodeURIComponent(id)}/cancel`, { method: "POST" });
  return jsonOrThrow<{ ok: true; batch: DirectDebitBatch }>(res);
}

export async function deleteDirectDebitBatchApi(id: string): Promise<void> {
  const res = await fetch(`/api/direct-debit-batches/${encodeURIComponent(id)}`, { method: "DELETE" });
  await jsonOrThrow<{ ok: true }>(res);
}
